/**
 * CharacterController - First Person Character Controller with Collisions
 *
 * A lightweight character controller for PlayCanvas that provides:
 * - WASD/Arrow key movement
 * - Mouse look
 * - Gravity and ground detection via raycasting
 * - Collision detection with scene meshes via raycasting
 *
 * Does NOT require ammo.js physics - uses simple raycasting for collisions.
 */
import * as pc from 'playcanvas';
import { VoxelCollision } from './VoxelCollision';
import { lookAxisSign } from './lookInversion';
import { MeshCollision } from './MeshCollision';
import { SplatOccupancyGrid, PICK_SPLAT_EXACT } from './SplatOccupancyGrid';
import { readGamepad, GP_BUTTON } from './gamepadUtils';
import { fileUnitsPerWorldUnit, filePushOutToWorld } from './splatCollisionSpace';
import { findNearestFreeSpot, isEnclosed } from './voxelRescue';
import { disableHelperShadows } from '../utils/helperShadows';
import { addContainerMeshes, attachTriangleCollider, colliderFileName, isLoadableColliderFile, triangleColliderOf } from './customMeshCollider';
import { colliderBox, type ColliderBox } from './colliderBox';

/**
 * The subset of collision-query methods that CharacterController needs.
 * Both VoxelCollision and MeshCollision implement this shape, which lets us
 * route through `activeCollision` at runtime based on `collisionSystem`.
 */
type ActiveCollision = Pick<
    VoxelCollision,
    'findGround' | 'findGroundInvertedY' | 'querySphere' | 'checkSphereSolid' | 'collectSolidAABBs' | 'getMetadata'
>;

/**
 * Narrow point/sphere occupancy contract used by raycastSurface (the reticle). VoxelCollision,
 * MeshCollision, and SplatOccupancyGrid all satisfy it, so the reticle can raycast authored
 * collision when present and an auto-built splat grid when not.
 */
interface SurfaceQuery {
    checkPointSolid(wx: number, wy: number, wz: number): boolean;
    checkSphereSolid(wx: number, wy: number, wz: number, radius: number): boolean;
    /** Optional natural query resolution (LOCAL units) so the reticle sweep can keep its sphere
     *  radius ~1 cell (O(1) per sample). Grid + voxel provide it; mesh omits it. */
    queryCellSize?(): number;
    /** Optional per-cell coverage in [0,1] (LOCAL space). Only the splat grid implements it; its
     *  presence switches sweepSurfaceT to the sustained-opacity march so the ray passes through thin
     *  faint blobs and lands on the real surface (voxel stays first-hit). */
    cellAlphaAt?(wx: number, wy: number, wz: number): number;
    /** Optional per-splat gaussian refinement inside an ACCEPTED run window (LOCAL-space ray). Only
     *  the splat grid implements it; null → caller keeps the grid-cell mean (never regresses). */
    refineHitLocal?(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number,
        tFront: number, tBack: number, tGridLocal?: number, worldPerLocal?: number): number | null;
    /** Optional splat-EXACT pick (LOCAL-space ray, dir normalized in LOCAL space, tMax in local
     *  units): acceptance runs against the actual gaussians — the grid is pure acceleration. Only
     *  the splat grid implements it; gate on supportsExactPick. When active, an exact miss is a
     *  MISS — the cell march must NOT run behind it. */
    raycastSplatExactLocal?(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number,
        tMax: number): number | null;
    /** True when raycastSplatExactLocal has real per-splat opacity to composite (splat grid only). */
    readonly supportsExactPick?: boolean;
}

// Reusable scratch vectors for raycastSurface (single-threaded, avoid per-sample GC).
const _rcWorld = new pc.Vec3();
const _rcFile = new pc.Vec3();

/**
 * Ray-vs-axis-aligned-box (slab method). Returns the nearest entry distance t (along the normalized
 * ray) within [~0, maxDist], or null on a miss. Used to pick exact hit points on primitive / GLB
 * colliders (stored as world-space AABBs). An entry at ~0 (origin inside the box — e.g. a dome
 * collider around the camera) is rejected so picking doesn't snap to the lens.
 */
function rayAabbEntry(
    ox: number, oy: number, oz: number, dx: number, dy: number, dz: number,
    cx: number, cy: number, cz: number, hx: number, hy: number, hz: number, maxDist: number,
): number | null {
    let tmin = 0, tmax = maxDist;
    const o = [ox, oy, oz];
    const d = [dx, dy, dz];
    const lo = [cx - hx, cy - hy, cz - hz];
    const hi = [cx + hx, cy + hy, cz + hz];
    for (let i = 0; i < 3; i++) {
        if (Math.abs(d[i]) < 1e-9) {
            if (o[i] < lo[i] || o[i] > hi[i]) return null; // parallel to slab & outside it
        } else {
            const inv = 1 / d[i];
            let t1 = (lo[i] - o[i]) * inv, t2 = (hi[i] - o[i]) * inv;
            if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
            if (t1 > tmin) tmin = t1;
            if (t2 < tmax) tmax = t2;
            if (tmin > tmax) return null;
        }
    }
    return tmin > 1e-4 ? tmin : null; // reject entry at/behind origin (inside-box / degenerate)
}
/**
 * Calculate the damp rate for smooth interpolation
 * Same formula as PlayCanvas's camera-controls for consistent feel
 */
const damp = (damping: number, dt: number): number => 1 - Math.pow(damping, dt * 1000);
export interface CharacterControllerConfig {
    /** Movement speed in units per second */
    moveSpeed?: number;
    /** Sprint speed multiplier */
    sprintMultiplier?: number;
    /** Mouse look sensitivity */
    lookSensitivity?: number;
    /**
     * Invert drag-to-look on BOTH axes (scene setting `invertCameraRotation`, editor "Invert Drag
     * Direction") — mouse-look and mobile drag-look drag the scene instead of the view, matching
     * explore mode. Gamepad sticks keep the conventional invert-Y meaning. See lookInversion.ts.
     */
    invertCameraRotation?: boolean;
    /** Player height (camera Y offset from ground) */
    playerHeight?: number;
    /** Gravity strength (units per second squared) */
    gravity?: number;
    /** Maximum fall speed */
    maxFallSpeed?: number;
    /** Jump velocity */
    jumpVelocity?: number;
    /** Collision radius for horizontal movement */
    collisionRadius?: number;
    /** Step height for climbing small obstacles */
    stepHeight?: number;
    /** Ground check distance below feet */
    groundCheckDistance?: number;
    /** Movement dampening for smooth start/stop (0-1, higher = more smoothing) */
    moveDamping?: number;
    /** Enable game-controller (gamepad) input for walking. Defaults to true. */
    gamepadEnabled?: boolean;
}
interface CollisionMeshData {
    entity?: pc.Entity;
    meshType: string;
    position?: number[] | { x: number; y: number; z: number };
    rotation?: number[] | { x: number; y: number; z: number };
    scaling?: number[] | { x: number; y: number; z: number };
    customMeshUrl?: string;
    /** Inline vertex data (flat xyz triples) for custom collision shapes — or, for an uploaded
     *  model, the editor's record of its file name (the parser hint for a blob: URL). */
    meshData?: number[] | string;
    /** "Inside"/containment mode (sphere): keep the player WITHIN the volume. */
    inside?: boolean;
}
/** Extract xyz from number[] or {x,y,z} format */
function toXYZ(v: number[] | { x: number; y: number; z: number } | undefined, fallback: [number, number, number]): [number, number, number] {
    if (!v) return fallback;
    if (Array.isArray(v)) return [v[0] ?? fallback[0], v[1] ?? fallback[1], v[2] ?? fallback[2]];
    return [v.x ?? fallback[0], v.y ?? fallback[1], v.z ?? fallback[2]];
}
interface CollisionEntity extends pc.Entity {
    _collisionBounds?: pc.BoundingBox;
    _collisionMeshType?: string;
    /** "Inside" / containment mode: keep the player WITHIN this volume (e.g. a
     *  dome) instead of blocking entry. Currently used for sphere colliders. */
    _collisionInsideMode?: boolean;
}
/**
 * CharacterController class - First person controller with collision detection
 */
export class CharacterController {
    private camera: pc.Entity;
    private app: pc.Application;
    private enabled: boolean = false;
    // Movement state
    private velocity: pc.Vec3 = new pc.Vec3();
    private isGrounded: boolean = false;
    private yaw: number = 0;
    private pitch: number = 0;
    // Input state
    private keys: {
        [key: string]: boolean;
    } = {};

    // Configuration
    moveSpeed: number = 8; // Increased for faster movement
    sprintMultiplier: number = 2;
    lookSensitivity: number = 0.002;
    // Extra sensitivity for the mobile Street-View drag-look (applyLookDelta) — a finger drag feels
    // good a bit hotter than mouse-look. degPerPixel = lookSensitivity * 100 * touchLookMultiplier.
    touchLookMultiplier: number = 1.5;
    // Invert drag-to-look on both axes (mouse-look + mobile drag-look) — same toggle as explore
    // mode's invertCameraRotation, kept in sync from the scene settings.
    invertCameraRotation: boolean = false;
    playerHeight: number = 1.6;
    gravity: number = 20;
    maxFallSpeed: number = 50;
    jumpVelocity: number = 8;
    collisionRadius: number = 0.3;
    stepHeight: number = 0.3;
    groundCheckDistance: number = 0.1;
    moveDamping: number = 0.9; // Smooth movement like BabylonJS
    autoMoveForward: boolean = false;
    autoMoveSpeedFactor: number = 1.0;
    headBobEnabled: boolean = true;
    // Velocity smoothing for gradual start/stop
    private horizontalVelocity: pc.Vec3 = new pc.Vec3();
    private targetVelocity: pc.Vec3 = new pc.Vec3();
    // Head bob state
    private bobTimer: number = 0;
    private bobOffsetY: number = 0;
    private bobOffsetX: number = 0;
    // Head bob constants
    private static readonly WALK_BOB_FREQ = 10;
    private static readonly WALK_BOB_AMP_Y = 0.025;
    private static readonly WALK_BOB_AMP_X = 0.012;
    private static readonly SPRINT_BOB_FREQ = 14;
    private static readonly SPRINT_BOB_AMP_Y = 0.04;
    private static readonly SPRINT_BOB_AMP_X = 0.02;
    // Coyote time + jump buffering
    private currentTime: number = 0;
    private lastGroundedTime: number = -Infinity;
    private lastJumpPressTime: number = -Infinity;
    private coyoteTime: number = 0.15;
    private jumpBufferTime: number = 0.15;
    // Landing camera dip
    private landingDipAmount: number = 0;
    private previousVelocityY: number = 0;
    private wasGroundedLastFrame: boolean = false;
    private static readonly LANDING_DIP_DECAY = 8;
    private static readonly MAX_LANDING_DIP = 0.15;
    // Collision meshes
    private collisionEntities: pc.Entity[] = [];
    private floorEntity: pc.Entity | null = null;
    // Colliders built from the scene data at load (createCollisionMeshes), each with the model it
    // loaded (null for a primitive). The editor's live colliders replace them
    // (releaseSceneCollisionMeshes); a model still loading then is dropped when it arrives.
    private readonly sceneColliders = new Map<pc.Entity, pc.Asset | null>();
    private _sceneCollidersToken = 0;
    private readonly _box: ColliderBox = { cx: 0, cy: 0, cz: 0, hx: 0, hy: 0, hz: 0 };
    // Voxel octree collision (optional, loaded async)
    private voxelCollision: VoxelCollision | null = null;
    private _lastVoxelUrl: string | null = null;
    private _voxelLoadToken: number = 0;
    // Triangle-mesh collision (optional, loaded async). Same query surface as voxel,
    // but driven by the .collision.glb generated by splat-transform or fused from LCC2.
    private meshCollision: MeshCollision | null = null;
    private _lastMeshUrl: string | null = null;
    // Loaded collision by URL, so switching between splats' collision (splat swaps with
    // their own collision) re-activates a set instead of downloading it again.
    private readonly _voxelCache = new Map<string, VoxelCollision>();
    private readonly _meshCache = new Map<string, MeshCollision>();
    private _meshLoadToken: number = 0;
    // Which loaded representation is being queried right now. Defaults to voxel;
    // if the selected representation is unavailable, fallback prefers voxel.
    private collisionSystem: 'voxel' | 'mesh' = 'voxel';
    // Master toggle for splat-derived collision. When false, voxel/mesh queries
    // are skipped entirely (primitive collision still applies). Used by the
    // editor's "Use auto nav mesh" switch so creators can A/B test or disable
    // the auto-generated nav data without clearing it.
    private splatCollisionEnabled: boolean = true;
    private get activeCollision(): ActiveCollision | null {
        if (!this.splatCollisionEnabled) return null;
        if (!this._splatEntity) return null;
        if (this.collisionSystem === 'mesh' && this.meshCollision) return this.meshCollision;
        if (this.collisionSystem === 'voxel' && this.voxelCollision) return this.voxelCollision;
        // Fallback: use whichever is loaded if the preferred one isn't available.
        return this.voxelCollision ?? this.meshCollision;
    }
    // Splat entity reference — needed for world↔file coordinate transforms in voxel queries
    private _splatEntity: pc.Entity | null = null;
    private _invSplatMatrix: pc.Mat4 = new pc.Mat4();
    // Lazily-built OPACITY-MASS occupancy grid from the splat points — the universal pick fallback
    // for scenes with no authored mesh/voxel collision. Cells go solid only where enough splat
    // OPACITY accumulates, so faint airborne floaters never register and picks land on the real
    // surface (the old count grid still dropped picks in mid-air on dense-but-faint floater blobs).
    // Coarse (~scene/256): fine for cursor/click-to-move, not measurement-grade. See
    // SplatOccupancyGrid. _splatOpacity caches per-splat LOGIT opacity (aligned to centers); the
    // 'pending' state covers SOG whose opacity decompresses asynchronously.
    private _splatGrid: SplatOccupancyGrid | null = null;
    private _splatOpacity: Float32Array | null = null;
    private _splatScales: Float32Array | null = null;  // log-space scale_0/1/2 interleaved (per-splat refine)
    private _splatOpacityState: 'idle' | 'pending' | 'ready' = 'idle';
    // Splat occupancy-grid pick removed — raycastSurface + hasSplatSurfaceCollision use only real
    // collision (primitive colliders / baked mesh / voxel), never the coarse splat-center grid.
    private allowSplatGridPick = false;
    // Models used as collision (collisionMeshesData meshType 'custom') carry a precise triangle
    // collider on their entity (customMeshCollider.ts: triangleColliderOf). Walk, fly, the drone and
    // picking all use it instead of the entity's bounding box.
    private _splatMatrix: pc.Mat4 = new pc.Mat4();
    // Event handlers (for cleanup)
    private keydownHandler: ((e: KeyboardEvent) => void) | null = null;
    private keyupHandler: ((e: KeyboardEvent) => void) | null = null;
    private mousemoveHandler: ((e: MouseEvent) => void) | null = null;
    private mousedownHandler: ((e: MouseEvent) => void) | null = null;
    private mouseupHandler: ((e: MouseEvent) => void) | null = null;
    private mouseIsDown: boolean = false;
    private lastMouseX: number = 0;
    private lastMouseY: number = 0;
    // Mobile joystick input (normalized -1 to 1)
    private joystickMoveX: number = 0;
    private joystickMoveZ: number = 0;
    private joystickLookX: number = 0;
    private joystickLookY: number = 0;
    // Game controller (gamepad) input
    gamepadEnabled: boolean = true;
    /** Right-stick look speed in degrees/second at full deflection. */
    gamepadLookSpeed: number = 140;
    private gpJumpHeld: boolean = false;
    // Walk-to-target state (double-click to walk)
    private _walkTarget: pc.Vec3 | null = null;
    private _walkTargetArrivalDist: number = 0.5;
    // Temporary vectors for calculations
    private tmpVec = new pc.Vec3();
    private tmpVec2 = new pc.Vec3();
    private tmpVec3 = new pc.Vec3();
    private tmpSweepVec = new pc.Vec3();
    // Voxel push-out scratch (voxelPushOut / isVoxelWedged) — separate from tmpVec*, which
    // update() and checkGround hold across those calls.
    private _voxelFilePos = new pc.Vec3();
    private _voxelPush = new pc.Vec3();
    private _wedgePos = new pc.Vec3();
    private _wedgePushA = new pc.Vec3();
    private _wedgePushB = new pc.Vec3();
    // Voxel rescue (voxelRescue.ts). Voxel walls are solid and a too-narrow gap stops the walker,
    // and voxel sets are generated from the splat, so an existing scene can hold stray voxels at
    // eye height. A body that starts embedded in voxels (spawn, walk-mode entry, a teleport or
    // waypoint jump, a newly activated collision set) is moved to the nearest free spot; a body
    // boxed in by voxels (no way out it fits through) walks through voxels until it is out.
    private _voxelPlacementPending = true;
    private _lastPhysicsPos = new pc.Vec3();
    private _hasLastPhysicsPos = false;
    /** Horizontal voxel collision is off until the body is free again and no longer boxed in. */
    private _voxelEscape = false;
    private _voxelBlockedTime = 0;
    /** Boxed-in checks that found a way out since the walker last moved freely (backoff). */
    private _voxelTrapChecks = 0;
    private _voxelEscapeCheckTime = -Infinity;
    private _rescueProbe = new pc.Vec3();
    private _rescueFilePos = new pc.Vec3();
    /** Half-height of the rescue's horizontal body band, as a fraction of the body radius. */
    private static readonly RESCUE_BAND_FRACTION = 0.35;
    /** Small spheres sampling the band's outer ring (see voxelBodyOverlaps). */
    private static readonly RESCUE_BAND_RING = 10;
    /** A larger jump between two frames is treated as a new placement (teleport, waypoint jump). */
    private static readonly TELEPORT_DISTANCE = 1;
    /** How far (m) the rescue looks for a free spot, and how far a way out must reach. */
    private static readonly RESCUE_SEARCH_RADIUS = 2;
    /** Pressing to move but (almost) not moving this long triggers the boxed-in check. */
    private static readonly TRAP_BLOCKED_SECONDS = 1;
    /** Longest wait between two boxed-in checks while the walker keeps pressing against a wall. */
    private static readonly TRAP_MAX_WAIT_SECONDS = 8;
    /** While escaping, how often the boxed-in check is repeated once the body is free. */
    private static readonly ESCAPE_RECHECK_SECONDS = 0.25;
    private tmpMatrix = new pc.Mat4();
    private forward = new pc.Vec3();
    private right = new pc.Vec3();
    constructor(camera: pc.Entity, app: pc.Application, config: CharacterControllerConfig = {}) {
        this.camera = camera;
        this.app = app;
        // Apply config
        if (config.moveSpeed !== undefined)
            this.moveSpeed = config.moveSpeed;
        if (config.sprintMultiplier !== undefined)
            this.sprintMultiplier = config.sprintMultiplier;
        if (config.lookSensitivity !== undefined)
            this.lookSensitivity = config.lookSensitivity;
        if (config.invertCameraRotation !== undefined)
            this.invertCameraRotation = config.invertCameraRotation;
        if (config.playerHeight !== undefined)
            this.playerHeight = config.playerHeight;
        if (config.gravity !== undefined)
            this.gravity = config.gravity;
        if (config.maxFallSpeed !== undefined)
            this.maxFallSpeed = config.maxFallSpeed;
        if (config.jumpVelocity !== undefined)
            this.jumpVelocity = config.jumpVelocity;
        if (config.collisionRadius !== undefined)
            this.collisionRadius = config.collisionRadius;
        if (config.stepHeight !== undefined)
            this.stepHeight = config.stepHeight;
        if (config.groundCheckDistance !== undefined)
            this.groundCheckDistance = config.groundCheckDistance;
        if (config.moveDamping !== undefined)
            this.moveDamping = config.moveDamping;
        if (config.gamepadEnabled !== undefined)
            this.gamepadEnabled = config.gamepadEnabled;
        // Initialize yaw/pitch from camera rotation
        const angles = this.camera.getEulerAngles();
        this.pitch = angles.x;
        this.yaw = angles.y;
    }
    /**
     * Create collision meshes from scene data
     */
    async createCollisionMeshes(collisionMeshesData: CollisionMeshData[]): Promise<void> {
        if (!collisionMeshesData || collisionMeshesData.length === 0)
            return;
        console.log('[CharacterController] Creating collision meshes:', collisionMeshesData.length);
        const loadPromises: Promise<void>[] = [];
        const token = this._sceneCollidersToken;
        collisionMeshesData.forEach((data, index) => {
            // Handle custom mesh (GLB/GLTF files)
            if (data.meshType === 'custom' && data.customMeshUrl) {
                const promise = this.loadCustomCollisionMesh(data, index, token);
                loadPromises.push(promise);
                return;
            }
            // Handle custom mesh with inline vertex data (meshData)
            if (data.meshType === 'custom' && Array.isArray(data.meshData) && data.meshData.length >= 9) {
                const entity = new pc.Entity(`collision-custom-meshdata-${index}`);
                this.configureCollisionEntity(entity, data);
                this.computeBoundsFromMeshData(entity, data.meshData);
                return;
            }
            let entity: pc.Entity | null = null;
            switch (data.meshType) {
                case 'cube':
                    entity = new pc.Entity(`collision-cube-${index}`);
                    entity.addComponent('render', {
                        type: 'box'
                    });
                    break;
                case 'sphere':
                    entity = new pc.Entity(`collision-sphere-${index}`);
                    entity.addComponent('render', {
                        type: 'sphere'
                    });
                    break;
                case 'torus':
                    entity = new pc.Entity(`collision-torus-${index}`);
                    entity.addComponent('render', {
                        type: 'torus'
                    });
                    break;
                case 'floor':
                    entity = new pc.Entity(`collision-floor-${index}`);
                    entity.addComponent('render', {
                        type: 'plane'
                    });
                    this.floorEntity = entity;
                    break;
                case 'plane':
                default:
                    entity = new pc.Entity(`collision-plane-${index}`);
                    entity.addComponent('render', {
                        type: 'plane'
                    });
                    break;
            }
            if (entity) {
                this.configureCollisionEntity(entity, data);
            }
        });
        // Wait for all custom meshes to load
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }
        console.log('[CharacterController] Created', this.collisionEntities.length, 'collision entities');
    }
    /**
     * Load a custom collision mesh (GLB/GLTF)
     */
    private async loadCustomCollisionMesh(data: CollisionMeshData, index: number, token: number): Promise<void> {
        const url = data.customMeshUrl;
        if (!url) return;
        // PlayCanvas only ships with GLB/GLTF parsers. Skip unsupported formats
        // upfront rather than firing an asset-load error — a failed load could
        // leave a pending asset in the registry and confuses users reading logs.
        // Other mesh formats (.obj, .fbx, .stl, etc.) must be converted to GLB (the editor converts
        // OBJ uploads). A blob: URL has no extension: the editor keeps the file name in meshData.
        const fileName = colliderFileName(url, data.meshData);
        if (!isLoadableColliderFile(fileName)) {
            console.warn(
                `[CharacterController] Unsupported collision mesh format ".${fileName.split('.').pop()?.toLowerCase() ?? ''}" — ` +
                `only .glb and .gltf are supported. Skipping: ${url}. ` +
                `Convert the mesh to GLB, or use voxel collision / primitives instead.`
            );
            return;
        }
        console.log('[CharacterController] Loading custom collision mesh:', url);
        const asset = new pc.Asset(`collision-custom-${index}`, 'container', url.startsWith('blob:') ? { url, filename: fileName } : { url });
        try {
            await new Promise<void>((resolve, reject) => {
                asset.ready(() => {
                    // The editor took the colliders over while this loaded (releaseSceneCollisionMeshes).
                    if (token !== this._sceneCollidersToken) {
                        this.app.assets.remove(asset);
                        asset.unload();
                        resolve();
                        return;
                    }
                    try {
                        const entity = new pc.Entity(`collision-custom-${index}`);
                        // GLB/GLTF container - instantiate the model
                        const containerResource = asset.resource as pc.ContainerResource;
                        if (containerResource) {
                            addContainerMeshes(entity, containerResource);
                        }
                        this.configureCollisionEntity(entity, data);
                        this.sceneColliders.set(entity, asset);
                        // Store bounding box for custom mesh collision (AABB — walk collision + fallback).
                        this.computeAndStoreBounds(entity);
                        // Build a PRECISE triangle collider from the GLB so picking lands exactly on
                        // its surface (like the LCC2 collision mesh), not the loose bounding box.
                        this.buildCustomMeshCollider(entity);
                        resolve();
                    }
                    catch (err) {
                        console.error('[CharacterController] Error setting up custom mesh:', err);
                        reject(err);
                    }
                });
                asset.on('error', (err: unknown) => {
                    console.error('[CharacterController] Error loading custom mesh:', url, err);
                    reject(err);
                });
                this.app.assets.add(asset);
                this.app.assets.load(asset);
            });
        }
        catch (error) {
            console.error('[CharacterController] Failed to load custom collision mesh:', url, error);
            // Clean up failed asset so it doesn't linger in the registry
            try { this.app.assets.remove(asset); } catch { /* ignore */ }
        }
    }

    /**
     * Give a loaded GLB collider entity its precise triangle collider (customMeshCollider.ts), so
     * walking, flying and picking use the model's real surface (like the LCC2 collision mesh)
     * instead of the entity's bounding box. Without triangles the bounding box stays the fallback.
     */
    private buildCustomMeshCollider(entity: pc.Entity): void {
        try {
            const mc = attachTriangleCollider(entity);
            if (mc) console.log('[CharacterController] Built triangle collider from GLB upload:', mc.triangleCount, 'tris');
        } catch (err) {
            console.warn('[CharacterController] Custom mesh collider extraction failed (AABB fallback):', err);
        }
    }

    /**
     * Compute and store bounding box for a collision entity
     */
    private computeAndStoreBounds(entity: pc.Entity): void {
        // Traverse entity and its children to compute combined bounds
        const bounds = new pc.BoundingBox();
        let boundsInitialized = false;
        const traverse = (node: pc.Entity) => {
            if (node.render && node.render.meshInstances) {
                for (const mi of node.render.meshInstances) {
                    if (mi.aabb) {
                        if (!boundsInitialized) {
                            bounds.copy(mi.aabb);
                            boundsInitialized = true;
                        }
                        else {
                            bounds.add(mi.aabb);
                        }
                    }
                }
            }
            for (const child of node.children) {
                if (child instanceof pc.Entity) {
                    traverse(child);
                }
            }
        };
        traverse(entity);
        if (boundsInitialized) {
            (entity as CollisionEntity)._collisionBounds = bounds;
        }
    }
    /**
     * Compute world-space AABB from inline vertex data (meshData).
     * meshData is a flat array of xyz triples in BabylonJS local space.
     */
    private computeBoundsFromMeshData(entity: pc.Entity, meshData: number[]): void {
        // Compute local-space AABB from vertex data
        // Negate Z to convert from BabylonJS to PlayCanvas local space
        const localMin = new pc.Vec3(Infinity, Infinity, Infinity);
        const localMax = new pc.Vec3(-Infinity, -Infinity, -Infinity);
        for (let i = 0; i < meshData.length; i += 3) {
            const x = meshData[i];
            const y = meshData[i + 1];
            const z = -meshData[i + 2]; // Negate Z for coordinate system conversion
            localMin.x = Math.min(localMin.x, x);
            localMin.y = Math.min(localMin.y, y);
            localMin.z = Math.min(localMin.z, z);
            localMax.x = Math.max(localMax.x, x);
            localMax.y = Math.max(localMax.y, y);
            localMax.z = Math.max(localMax.z, z);
        }
        // Transform all 8 corners of the local AABB through the entity's world transform
        const worldTransform = entity.getWorldTransform();
        const corners = [
            new pc.Vec3(localMin.x, localMin.y, localMin.z),
            new pc.Vec3(localMax.x, localMin.y, localMin.z),
            new pc.Vec3(localMin.x, localMax.y, localMin.z),
            new pc.Vec3(localMax.x, localMax.y, localMin.z),
            new pc.Vec3(localMin.x, localMin.y, localMax.z),
            new pc.Vec3(localMax.x, localMin.y, localMax.z),
            new pc.Vec3(localMin.x, localMax.y, localMax.z),
            new pc.Vec3(localMax.x, localMax.y, localMax.z),
        ];
        const worldMin = new pc.Vec3(Infinity, Infinity, Infinity);
        const worldMax = new pc.Vec3(-Infinity, -Infinity, -Infinity);
        for (const corner of corners) {
            worldTransform.transformPoint(corner, corner);
            worldMin.x = Math.min(worldMin.x, corner.x);
            worldMin.y = Math.min(worldMin.y, corner.y);
            worldMin.z = Math.min(worldMin.z, corner.z);
            worldMax.x = Math.max(worldMax.x, corner.x);
            worldMax.y = Math.max(worldMax.y, corner.y);
            worldMax.z = Math.max(worldMax.z, corner.z);
        }
        const bounds = new pc.BoundingBox();
        bounds.center.set(
            (worldMin.x + worldMax.x) / 2,
            (worldMin.y + worldMax.y) / 2,
            (worldMin.z + worldMax.z) / 2
        );
        bounds.halfExtents.set(
            (worldMax.x - worldMin.x) / 2,
            (worldMax.y - worldMin.y) / 2,
            (worldMax.z - worldMin.z) / 2
        );
        (entity as CollisionEntity)._collisionBounds = bounds;
    }
    /**
     * Configure a collision entity with transform and visibility
     */
    private configureCollisionEntity(entity: pc.Entity, data: CollisionMeshData): void {
        // Apply transform - convert from BabylonJS coordinates
        const pos = toXYZ(data.position, [0, 0, 0]);
        entity.setPosition(pos[0], pos[1], -pos[2]); // Negate Z
        // Convert BabylonJS rotation (YXZ intrinsic, radians, left-handed) to PlayCanvas quaternion.
        // Same bjsRotationToPC pattern used by hotspots and HtmlMeshHelper.
        const rot = toXYZ(data.rotation, [0, 0, 0]);
        const rx = rot[0], ry = rot[1], rz = rot[2];
        const hy = ry / 2, hx = rx / 2, hz = rz / 2;
        const cy = Math.cos(hy), sy = Math.sin(hy);
        const cx = Math.cos(hx), sx = Math.sin(hx);
        const cz = Math.cos(hz), sz = Math.sin(hz);
        // Quaternion in BJS left-handed space (YXZ order)
        const qw = cy * cx * cz + sy * sx * sz;
        const qx = cy * sx * cz + sy * cx * sz;
        const qy = sy * cx * cz - cy * sx * sz;
        const qz = cy * cx * sz - sy * sx * cz;
        // Convert LH → RH (Z-flip): negate x and y
        const userRot = new pc.Quat(-qx, -qy, qz, qw);
        if (data.meshType === 'plane') {
            // PlayCanvas plane is XZ (horizontal); BabylonJS plane is XY (vertical, faces +Z).
            // Compose a 90° X tilt with user rotation — same as applyPlaneRotation for hotspots.
            const faceForward = new pc.Quat().setFromEulerAngles(90, 0, 0);
            entity.setRotation(new pc.Quat().mul2(userRot, faceForward));
        } else {
            entity.setRotation(userRot);
        }
        const scale = toXYZ(data.scaling, [1, 1, 1]);
        // PlayCanvas primitives are 1x1x1; BabylonJS creates them at larger base sizes.
        // Pre-multiply so the entity scale represents the true world size.
        const meshType = data.meshType;
        if (meshType === 'plane') {
            // BabylonJS CreatePlane({size:3}) is in XY (vertical). PlayCanvas plane is in XZ,
            // then we rotate 90° X to make it vertical. That rotation swaps local Y↔Z in world:
            //   PC local X → world X (width)    = BJS scaleX * 3
            //   PC local Z → world Y (height)   = BJS scaleY * 3
            //   PC local Y → world Z (thickness) = near-zero
            const THIN = 0.01;
            entity.setLocalScale(scale[0] * 3, scale[2] * THIN, scale[1] * 3);
        } else if (meshType === 'cube' || meshType === 'sphere' || meshType === 'torus') {
            // BabylonJS CreateBox({size:3}), CreateSphere({diameter:3}); torus has no
            // BJS legacy but uses the same x3 base so a fresh one matches cube/sphere size
            entity.setLocalScale(scale[0] * 3, scale[1] * 3, scale[2] * 3);
        } else if (meshType === 'floor') {
            // BabylonJS CreateGround({width:100, height:100}) — Y stays 1 (flat plane)
            entity.setLocalScale(scale[0] * 100, scale[1] * 1, scale[2] * 100);
        } else {
            entity.setLocalScale(scale[0], scale[1], scale[2]);
        }
        // Make invisible but keep for collision; never part of any shadow, should it be shown
        // (primitives and GLB children alike)
        this.setEntityVisibility(entity, false);
        disableHelperShadows(entity);
        // Store mesh type for collision logic
        (entity as CollisionEntity)._collisionMeshType = data.meshType;
        // "Inside"/containment mode (dome) — keep the player within this volume.
        (entity as CollisionEntity)._collisionInsideMode = data.inside === true;
        this.app.root.addChild(entity);
        this.sceneColliders.set(entity, null);
        // Precompute world-space AABB for primitives so collision detection
        // accounts for rotation (the AABB math only uses center + halfExtents).
        if (meshType !== 'custom') {
            this.computePrimitiveBounds(entity);
        }
        this.collisionEntities.push(entity);
    }
    /**
     * Compute world-space AABB for a primitive entity from its world transform.
     * PlayCanvas primitives span [-0.5, 0.5] locally; the world transform
     * bakes in position, rotation, AND scale, so the resulting AABB correctly
     * reflects the oriented + scaled volume.
     */
    private computePrimitiveBounds(entity: pc.Entity): void {
        const m = entity.getWorldTransform().data;
        const bounds = new pc.BoundingBox();
        bounds.center.set(m[12], m[13], m[14]);
        // Each column of the 3x3 part is a scaled basis vector.
        // World AABB half-extent on each axis = sum of abs column components * 0.5
        bounds.halfExtents.set(
            (Math.abs(m[0]) + Math.abs(m[4]) + Math.abs(m[8])) * 0.5,
            (Math.abs(m[1]) + Math.abs(m[5]) + Math.abs(m[9])) * 0.5,
            (Math.abs(m[2]) + Math.abs(m[6]) + Math.abs(m[10])) * 0.5
        );
        (entity as CollisionEntity)._collisionBounds = bounds;
    }
    /**
     * Set visibility of entity and all children
     */
    private setEntityVisibility(entity: pc.Entity, visible: boolean): void {
        if (entity.render) {
            entity.render.enabled = visible;
        }
        for (const child of entity.children) {
            if (child instanceof pc.Entity) {
                this.setEntityVisibility(child, visible);
            }
        }
    }
    /**
     * Enable the character controller
     */
    enable(): void {
        if (this.enabled)
            return;
        this.enabled = true;
        // Sync yaw/pitch from camera's forward vector (atan2-based).
        // getEulerAngles() is unreliable near gimbal lock and can return
        // flipped values. Deriving from the forward vector matches how
        // CameraControls.syncFromPose() does it for orbit↔fly transitions.
        const forward = new pc.Vec3(0, 0, -1);
        this.camera.getRotation().transformVector(forward, forward);
        const elev = Math.atan2(-forward.y, Math.sqrt(forward.x * forward.x + forward.z * forward.z)) * (180 / Math.PI);
        const azim = Math.atan2(-forward.x, -forward.z) * (180 / Math.PI);
        this.pitch = -elev;
        this.yaw = azim;
        // Reset velocities
        this.velocity.set(0, 0, 0);
        this.horizontalVelocity.set(0, 0, 0);
        this.targetVelocity.set(0, 0, 0);
        // Reset head bob and jump state
        this.bobTimer = 0;
        this.bobOffsetY = 0;
        this.bobOffsetX = 0;
        this.currentTime = 0;
        this.lastGroundedTime = -Infinity;
        this.lastJumpPressTime = -Infinity;
        this.landingDipAmount = 0;
        this.previousVelocityY = 0;
        this.wasGroundedLastFrame = false;
        // Entering walk mode is a placement: the first update checks the body against voxels.
        this._voxelPlacementPending = true;
        this._hasLastPhysicsPos = false;
        this._voxelBlockedTime = 0;
        this._voxelTrapChecks = 0;
        this._voxelEscape = false;
        this.updateSplatTransform();
        this.snapToGroundIfClose();
        // Setup input handlers
        this.setupInputHandlers();
        console.log('[CharacterController] Enabled');
    }
    /**
     * Disable the character controller
     */
    disable(): void {
        if (!this.enabled)
            return;
        this.enabled = false;
        this.autoMoveForward = false;
        // Clear joystick state to prevent stale input on re-enable
        this.joystickMoveX = 0;
        this.joystickMoveZ = 0;
        this.joystickLookX = 0;
        this.joystickLookY = 0;
        this.gpJumpHeld = false;
        // Remove input handlers
        this.removeInputHandlers();
        this.mouseIsDown = false;
        console.log('[CharacterController] Disabled');
    }
    /**
     * Setup keyboard and mouse input handlers
     */
    private setupInputHandlers(): void {
        const canvas = this.app.graphicsDevice.canvas as HTMLCanvasElement;
        // Keyboard handlers
        this.keydownHandler = (e: KeyboardEvent) => {
            // Skip when focus is in a text input or modal overlay (so typing doesn't move camera)
            const tag = (document.activeElement as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || (document.activeElement as HTMLElement)?.isContentEditable) return;
            this.keys[e.code] = true;
            // Record jump press time for coyote time + jump buffering
            if (e.code === 'Space') {
                this.lastJumpPressTime = this.currentTime;
            }
        };
        this.keyupHandler = (e: KeyboardEvent) => {
            this.keys[e.code] = false;
            // Variable jump height: cut upward velocity for short hop
            if (e.code === 'Space' && this.velocity.y > 0) {
                this.velocity.y *= 0.5;
            }
        };
        // Mouse look handler (left-click drag, no pointer lock)
        this.mousemoveHandler = (e: MouseEvent) => {
            if (!this.mouseIsDown) return;
            const dx = e.clientX - this.lastMouseX;
            const dy = e.clientY - this.lastMouseY;
            this.lastMouseX = e.clientX;
            this.lastMouseY = e.clientY;
            // Cancel auto-move / walk-to if user is rotating the camera
            if ((Math.abs(dx) + Math.abs(dy) > 2)) {
                if (this.autoMoveForward) this.autoMoveForward = false;
                if (this._walkTarget) this._walkTarget = null;
            }
            // First-person by default (drag right turns right); Invert Drag Direction flips BOTH axes so
            // the scene follows the pointer — the same toggle and math as explore-mode drags.
            const lookSign = lookAxisSign(this.invertCameraRotation);
            this.yaw -= dx * lookSign * this.lookSensitivity * 100;
            this.pitch -= dy * lookSign * this.lookSensitivity * 100;
            // Clamp pitch
            this.pitch = Math.max(-89, Math.min(89, this.pitch));
        };
        // Mousedown to start look drag
        this.mousedownHandler = (e: MouseEvent) => {
            if (e.button === 0) { // Left mouse button
                this.mouseIsDown = true;
                this.lastMouseX = e.clientX;
                this.lastMouseY = e.clientY;
            }
        };
        // Mouseup to stop look drag
        this.mouseupHandler = (e: MouseEvent) => {
            if (e.button === 0) {
                this.mouseIsDown = false;
            }
        };
        // Add event listeners
        document.addEventListener('keydown', this.keydownHandler);
        document.addEventListener('keyup', this.keyupHandler);
        document.addEventListener('mousemove', this.mousemoveHandler);
        canvas.addEventListener('mousedown', this.mousedownHandler);
        document.addEventListener('mouseup', this.mouseupHandler);
    }
    /**
     * Remove input handlers
     */
    private removeInputHandlers(): void {
        const canvas = this.app.graphicsDevice.canvas as HTMLCanvasElement;
        if (this.keydownHandler) {
            document.removeEventListener('keydown', this.keydownHandler);
        }
        if (this.keyupHandler) {
            document.removeEventListener('keyup', this.keyupHandler);
        }
        if (this.mousemoveHandler) {
            document.removeEventListener('mousemove', this.mousemoveHandler);
        }
        if (this.mousedownHandler) {
            canvas.removeEventListener('mousedown', this.mousedownHandler);
        }
        if (this.mouseupHandler) {
            document.removeEventListener('mouseup', this.mouseupHandler);
        }
        this.keys = {};
    }
    /**
     * Check if position collides with any collision mesh
     */
    private checkCollision(position: pc.Vec3, radius: number): boolean {
        // AABB collision check against collision entities
        const box = this._box;
        for (const entity of this.collisionEntities) {
            const meshType = (entity as CollisionEntity)._collisionMeshType;
            if (meshType === 'floor') {
                // Skip floor for horizontal collision (handled in checkGround)
                continue;
            }
            if ((entity as CollisionEntity)._collisionInsideMode) {
                // Containment ("inside") colliders aren't solid — they keep the
                // player WITHIN their volume via clampToInsideColliders().
                continue;
            }
            if (triangleColliderOf(entity)) {
                // Blocked precisely by its triangle mesh in resolveVoxelCollision, not this AABB.
                continue;
            }
            // World-space box: precomputed for scene colliders, from the live transform for editor ones.
            colliderBox(entity, box);
            const dx = Math.abs(position.x - box.cx);
            const dy = Math.abs(position.y - box.cy);
            const dz = Math.abs(position.z - box.cz);
            if (dx < box.hx + radius &&
                dy < box.hy + this.playerHeight / 2 &&
                dz < box.hz + radius) {
                return true;
            }
        }
        return false;
    }

    /**
     * Read-only world-space sphere collision query for other camera drivers.
     *
     * This deliberately follows the same authored collision state as walk mode:
     * solid primitives, precise uploaded GLB colliders, and the currently
     * selected/enabled generated mesh or voxel representation. Containment
     * ("inside") spheres are treated as boundaries, so a sphere wholly inside
     * is safe and one attempting to leave is a collision.
     */
    intersectsCollisionSphere(position: pc.Vec3, radius: number): boolean {
        if (this._splatEntity) this.updateSplatTransform();
        return this.intersectsCollisionSpherePrepared(position, radius);
    }

    /** Collision query with the splat transform already refreshed by the caller. */
    private intersectsCollisionSpherePrepared(position: pc.Vec3, radius: number): boolean {
        const r = Math.max(0.001, Number.isFinite(radius) ? radius : 0.001);
        const r2 = r * r;

        for (const entity of this.collisionEntities) {
            const ce = entity as CollisionEntity;

            if (ce._collisionInsideMode) {
                // Transform the center into the primitive's unit-sphere space.
                // For non-uniform scaling, independently subtracting the world
                // radius from each ellipsoid axis is not a valid Euclidean
                // erosion and can let the body clip through diagonal regions.
                // The minimum world-axis scale gives a conservative normalized
                // margin: it may crash slightly early on a stretched ellipsoid,
                // but it cannot label an intersecting body as safe.
                const world = entity.getWorldTransform();
                const m = world.data;
                const sx = Math.hypot(m[0], m[1], m[2]);
                const sy = Math.hypot(m[4], m[5], m[6]);
                const sz = Math.hypot(m[8], m[9], m[10]);
                if (sx < 1e-6 || sy < 1e-6 || sz < 1e-6) return true;
                this.tmpMatrix.copy(world).invert().transformPoint(position, this.tmpVec3);
                const minScale = Math.min(sx, sy, sz);
                const centerNorm = 2 * this.tmpVec3.length();
                const radiusNorm = 2 * r / minScale;
                if (centerNorm + radiusNorm > 1) return true;
                continue;
            }

            // Uploaded GLBs with extracted triangles are queried precisely;
            // testing their broad AABB too would create false crashes.
            const triangles = triangleColliderOf(entity);
            if (triangles) {
                if (triangles.checkSphereSolid(position.x, position.y, position.z, r)) return true;
                continue;
            }

            // Live editor-added primitives have no cached bounds: colliderBox derives
            // their world AABB from the full transform (rotation, parenting, scale).
            const { cx, cy, cz, hx, hy: boxHy, hz } = colliderBox(entity, this._box);

            // A floor is a surface, not the one-unit-thick box implied by its
            // legacy scale. Keep its collision slab thin so crashes occur at
            // the visible/configured floor height rather than half a unit up.
            const hy = ce._collisionMeshType === 'floor' ? Math.min(boxHy, 0.005) : boxHy;

            const dx = Math.max(Math.abs(position.x - cx) - hx, 0);
            const dy = Math.max(Math.abs(position.y - cy) - hy, 0);
            const dz = Math.max(Math.abs(position.z - cz) - hz, 0);
            if (dx * dx + dy * dy + dz * dz <= r2) return true;
        }

        const active = this.activeCollision;
        if (!active) return false;

        if (this.meshCollision !== null && active === this.meshCollision) {
            // Generated triangle collision is baked into world space.
            return active.checkSphereSolid(position.x, position.y, position.z, r);
        }

        // Voxel collision is stored in splat/file space. Conservatively map the
        // world-space radius into local space using the largest inverse-axis
        // scale. The caller refreshed the cached transform once for this query
        // or sweep, rather than once per sweep sample.
        this.worldToFile(position, this.tmpVec);
        return active.checkSphereSolid(this.tmpVec.x, this.tmpVec.y, this.tmpVec.z, this.worldSizeToFile(r));
    }

    /**
     * Conservative swept-sphere query. Samples overlap along the segment so a
     * fast camera cannot tunnel through a thin wall between frame endpoints.
     */
    sweepCollisionSphere(from: pc.Vec3, to: pc.Vec3, radius: number): boolean {
        const r = Math.max(0.001, Number.isFinite(radius) ? radius : 0.001);
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const dz = to.z - from.z;
        const distance = Math.hypot(dx, dy, dz);
        if (distance <= 1e-12) return this.intersectsCollisionSphere(from, r);

        const steps = Math.max(1, Math.ceil(distance / r));
        const sampleSpacing = distance / steps;
        // A row of radius-r spheres leaves tiny diagonal gaps. Sampling each
        // interval midpoint with this inflated radius encloses the whole swept
        // capsule: sqrt(r^2 + (spacing/2)^2). This is conservative without an
        // expensive analytic sweep against every collision representation.
        const probeRadius = Math.hypot(r, sampleSpacing * 0.5);
        if (this._splatEntity) this.updateSplatTransform();

        for (let i = 0; i < steps; i++) {
            const t = (i + 0.5) / steps;
            this.tmpSweepVec.set(from.x + dx * t, from.y + dy * t, from.z + dz * t);
            if (this.intersectsCollisionSpherePrepared(this.tmpSweepVec, probeRadius)) return true;
        }
        return false;
    }

    /**
     * Containment ("inside") colliders: keep the player WITHIN the volume instead
     * of blocking entry — e.g. a giant sphere used as a dome so the camera can
     * move freely inside but can't fly out. Ellipsoid clamp using each axis'
     * half-extent, so it works for non-uniform spheres and contains vertical
     * (fly) movement too. A sphere is rotation-invariant, so localScale * 0.5
     * gives exact per-axis radii (no need for the rotated AABB).
     */
    private clampToInsideColliders(pos: pc.Vec3): void {
        const r = this.collisionRadius;
        for (const entity of this.collisionEntities) {
            if (!(entity as CollisionEntity)._collisionInsideMode) continue;
            const c = entity.getPosition();
            const s = entity.getLocalScale();
            // Half-axes shrunk by the player radius; clamp tiny/over-shrunk axes.
            const hx = Math.max(0.01, s.x * 0.5 - r);
            const hy = Math.max(0.01, s.y * 0.5 - r);
            const hz = Math.max(0.01, s.z * 0.5 - r);
            const dx = pos.x - c.x;
            const dy = pos.y - c.y;
            const dz = pos.z - c.z;
            const norm = (dx * dx) / (hx * hx) + (dy * dy) / (hy * hy) + (dz * dz) / (hz * hz);
            if (norm > 1) {
                // Outside the ellipsoid — project back onto its surface.
                const k = 1 / Math.sqrt(norm);
                pos.x = c.x + dx * k;
                pos.y = c.y + dy * k;
                pos.z = c.z + dz * k;
            }
        }
    }

    /**
     * Resolve splat-derived collision (voxel or mesh) using push-out displacement.
     * Voxel queries run in file space (world→file→query→file→world); mesh queries
     * run directly in world space because MeshCollision pre-bakes the splat
     * transform into its triangles, decoupling queries from per-scene coord
     * conventions (LCC2's Z-up source vs Y-up engine, etc.).
     * `radius` is WORLD metres: the voxel path converts it into file units and the
     * push-out back, so the player's width never scales with the splat.
     * Returns true if any collision was resolved.
     */
    private resolveVoxelCollision(position: pc.Vec3, radius: number, allowVertical: boolean = true): boolean {
        let resolved = false;
        const apply = (px: number, py: number, pz: number) => {
            position.x += px;
            position.z += pz;
            if (allowVertical) position.y += py;
            if (allowVertical || Math.abs(px) > 1e-6 || Math.abs(pz) > 1e-6) resolved = true;
        };

        const active = this.activeCollision;
        if (active) {
            const isMesh = this.meshCollision !== null && active === this.meshCollision;
            if (isMesh) {
                // Mesh tris are world-space — query directly, push-out is already in world space.
                const pushOut = active.querySphere(position.x, position.y, position.z, radius);
                if (pushOut) apply(pushOut.x, pushOut.y, pushOut.z);
            } else {
                const worldPush = this._voxelPush;
                if (this.voxelPushOut(position, radius, worldPush)) apply(worldPush.x, worldPush.y, worldPush.z);
            }
        }

        // Custom GLB mesh colliders are triangle meshes baked to their entity's world transform, so
        // the player is pushed off their REAL surface, not a bounding box. Resolve each sequentially.
        for (const entity of this.collisionEntities) {
            const triangles = triangleColliderOf(entity);
            if (!triangles) continue;
            const pushOut = triangles.querySphere(position.x, position.y, position.z, radius);
            if (pushOut) apply(pushOut.x, pushOut.y, pushOut.z);
        }

        return resolved;
    }

    /**
     * One voxel push-out for a sphere of WORLD radius `radius` at world `pos`, written to `out` as
     * a world displacement; false when the voxel set isn't the active collision or nothing
     * overlaps. Voxels live in file space: the position goes through the splat's inverse matrix,
     * the radius is converted to file units (worldSizeToFile) and the push comes back at world
     * size (filePushToWorld). Passing the world radius unchanged — as this did before — made a
     * splat scaled by s collide the walker as if it were s times as wide.
     */
    private voxelPushOut(pos: pc.Vec3, radius: number, out: pc.Vec3): boolean {
        // Escaping a voxel pocket (updateVoxelTrap): no voxel push-out, and so no wedge stop.
        if (this._voxelEscape) return false;
        const active = this.activeCollision;
        if (!active || (this.meshCollision !== null && active === this.meshCollision)) return false;
        const filePos = this.worldToFile(pos, this._voxelFilePos);
        const fileRadius = this.worldSizeToFile(radius);
        const push = active.querySphere(filePos.x, filePos.y, filePos.z, fileRadius);
        if (!push) return false;
        this.filePushToWorld(push, fileRadius, radius, out);
        return true;
    }

    /**
     * True when the walker is wedged in a voxel gap narrower than its body: resolving the overlap
     * on one side pushes it into the opposite side, so a second push points back against the
     * first. VoxelCollision's push-out never pushes against its own previous push within a query
     * (that stops it oscillating), so on its own it would shove a too-wide walker sideways through
     * a doorway one frame at a time instead of stopping it. Horizontal only, like walk push-out.
     * A wall at an angle (a voxel staircase) keeps pushing the same way and is not a wedge.
     */
    private isVoxelWedged(pos: pc.Vec3, radius: number): boolean {
        const first = this._wedgePushA;
        if (!this.voxelPushOut(pos, radius, first)) return false;
        if (Math.hypot(first.x, first.z) < 1e-9) return false;
        const pushed = this._wedgePos.set(pos.x + first.x, pos.y, pos.z + first.z);
        const second = this._wedgePushB;
        if (!this.voxelPushOut(pushed, radius, second)) return false;
        const minResidual = Math.max(1e-3, radius * 0.02);
        return Math.hypot(second.x, second.z) > minResidual && first.x * second.x + first.z * second.z < 0;
    }

    /**
     * Splat surface geometry available for the reticle to raycast, IGNORING splatCollisionEnabled.
     * Prefers authored collision (voxel/mesh) when present; otherwise lazily builds a lightweight
     * occupancy grid from the splat's own points so the reticle works on EVERY splat — including
     * no-collision and primitive-only scenes — matching the old gsplat-pick behaviour without a
     * GPU pick render.
     */
    private get surfaceCollision(): SurfaceQuery | null {
        if (!this._splatEntity) return null;
        if (this.collisionSystem === 'mesh' && this.meshCollision) return this.meshCollision;
        if (this.collisionSystem === 'voxel' && this.voxelCollision) return this.voxelCollision;
        if (this.voxelCollision) return this.voxelCollision;
        if (this.meshCollision) return this.meshCollision;
        // No authored collision — fall back to an auto-built occupancy grid from the splat points.
        if (!this._splatGrid) this._splatGrid = this.buildSplatGrid();
        return this._splatGrid;
    }

    /**
     * Build a uniform occupancy grid from the splat's decoded point centers (local space).
     * Returns null until the splat resource (and its centers) are available — callers retry,
     * so the grid is built once the data arrives. Subsamples very large clouds to keep the
     * one-time build cheap; the grid still marks the surface cells.
     */
    /** Resolve the gsplat RESOURCE object (centers + gsplatData), mirroring splat-edit's order;
     *  _placement covers the unified (main) splat. */
    private _resolveSplatResource(): { centers?: Float32Array; gsplatData?: unknown } | null {
        type ResHolder = { resource?: { centers?: Float32Array; gsplatData?: unknown } };
        const gs = this._splatEntity?.gsplat as unknown as {
            instance?: ResHolder;
            _instance?: ResHolder;
            _placement?: ResHolder; // unified gsplats expose the resource here
            resource?: { centers?: Float32Array; gsplatData?: unknown };
            asset?: ResHolder;
        } | undefined;
        if (!gs) return null;
        return gs.instance?.resource
            ?? gs._instance?.resource
            ?? gs._placement?.resource
            ?? gs.resource
            ?? gs.asset?.resource
            ?? null;
    }

    /**
     * Lazily load per-splat PICK DATA — opacity (raw LOGIT) and log-space scales (scale_0/1/2,
     * interleaved), both aligned 1:1 with resource.centers — cached. Uncompressed PLY exposes
     * getProp directly; compressed PLY decompresses synchronously; SOG decompress returns a Promise
     * (async). Returns null while pending or when unavailable — the grid then falls back to its
     * count path (and refinement to the default σ). Logit→linear happens inside the grid.
     */
    private _ensureSplatOpacity(resource: { gsplatData?: unknown } | null): Float32Array | null {
        if (this._splatOpacityState === 'ready') return this._splatOpacity;
        if (this._splatOpacityState === 'pending') return null;
        if (!resource) return null;
        this._splatOpacityState = 'pending';
        type GsData = { decompress?: () => unknown; getProp?: (p: string) => Float32Array | null };
        const getProp = (d: GsData | null | undefined, p: string): Float32Array | null =>
            (d && typeof d.getProp === 'function') ? (d.getProp(p) ?? null) : null;
        const packScales = (d: GsData | null | undefined): Float32Array | null => {
            const s0 = getProp(d, 'scale_0'), s1 = getProp(d, 'scale_1'), s2 = getProp(d, 'scale_2');
            if (!s0 || !s1 || !s2) return null;   // any missing → no scales (refine uses default σ)
            const n = Math.min(s0.length, s1.length, s2.length);
            const out = new Float32Array(n * 3);
            for (let i = 0; i < n; i++) { out[i * 3] = s0[i]; out[i * 3 + 1] = s1[i]; out[i * 3 + 2] = s2[i]; }
            return out;
        };
        const finish = (d: GsData | null | undefined) => {
            this._splatOpacity = getProp(d, 'opacity');
            this._splatScales = packScales(d);
            this._splatOpacityState = 'ready';
        };
        try {
            const data = resource.gsplatData as GsData | undefined;
            if (!data) { finish(null); return null; }
            if (typeof data.decompress === 'function') {
                const d = data.decompress();                       // sync (compressed) | Promise (SOG)
                if (d instanceof Promise) {
                    (d as Promise<GsData>).then((dd) => finish(dd)).catch(() => finish(null));
                    return null;                                    // still pending → retried next frame
                }
                finish(d as GsData);
                return this._splatOpacity;
            }
            finish(data);
            return this._splatOpacity;
        } catch { finish(null); return null; }
    }

    private buildSplatGrid(): SplatOccupancyGrid | null {
        const resource = this._resolveSplatResource();
        // TODO(lod-pick): LOD-streaming (octree) splats resolve to a GSplatOctreeResource with no
        // resident `centers`, so this returns null and the auto grid never builds here. That's
        // acceptable today because CharacterController only exists when there IS authored collision,
        // and the reticle then raycasts that voxel/mesh — not this grid. If we ever need the grid
        // pick on a collision-free-but-LOD scene through this path, mirror SplatSurfaceRaycaster's
        // full-SOG fallback (needs the app + scene SOG URL wired in here).
        const centers: Float32Array | null | undefined = resource?.centers;
        if (!centers || centers.length < 9) return null;
        const opacity = this._ensureSplatOpacity(resource);
        if (this._splatOpacityState === 'pending') return null;   // wait for SOG decompress; retried next frame
        try {
            const n = Math.floor(centers.length / 3);
            const stride = n > 1_500_000 ? Math.ceil(n / 1_500_000) : 1;
            const grid = new SplatOccupancyGrid(centers, opacity, 256, stride, this._splatScales);
            return grid.pointCount > 0 ? grid : null;
        } catch {
            return null;
        }
    }

    /** True when REAL, pickable collision exists for the reticle to raycast: a solid primitive
     *  collider, a baked mesh, or generated voxels. The auto splat-center grid (opacity-mass) is
     *  included only when allowSplatGridPick is set — see _splatGrid. */
    get hasSplatSurfaceCollision(): boolean {
        if (this.meshCollision || this.voxelCollision) return true;
        for (const e of this.collisionEntities) {
            if (!(e as CollisionEntity)._collisionInsideMode) return true; // a solid primitive collider
        }
        return this.allowSplatGridPick && this.surfaceCollision !== null;
    }

    /**
     * Raycast the splat-derived collision surface (voxel or mesh) along a world-space ray and
     * return the world-space surface hit, or null on a miss within maxDist.
     *
     * This exists so the cursor reticle can find the surface point WITHOUT a GPU gsplat pick
     * render. In PlayCanvas 2.20 the unified gsplat uses a single shared work buffer + async
     * sorter; a second per-frame render of it (the old picker.prepare path) disturbs that shared
     * state and makes the splats shimmer. A CPU raycast against the same collision the camera
     * already uses removes that entirely and is cheaper (no GPU pass, no readback).
     *
     * Coordinate handling mirrors resolveVoxelCollision: mesh tris are pre-baked to world space
     * (query directly); voxel data is file space (round-trip through worldToFile). The coarse
     * sweep uses checkSphereSolid (radius ≈ step) so it cannot tunnel through the surface between
     * samples — step size only affects perf, not correctness — then a binary search refines to
     * the precise surface point.
     */
    raycastSurface(originWorld: pc.Vec3, dirWorld: pc.Vec3, maxDist: number): pc.Vec3 | null {
        // Keep the world→local matrices current. The reticle runs in explore mode too, where
        // update() (which normally refreshes them) is not called — without this a transformed
        // splat (editor gizmo / sceneScale) would be queried with a stale inverse matrix.
        if (this._splatEntity) this.updateSplatTransform();

        const len = Math.hypot(dirWorld.x, dirWorld.y, dirWorld.z) || 1;
        const nx = dirWorld.x / len, ny = dirWorld.y / len, nz = dirWorld.z / len;
        const dist = Math.max(1, maxDist);

        let bestT = Infinity;

        // Candidate A — primitive colliders (editor boxes/spheres/planes/floor + GLB-upload
        // colliders, each stored as a world-space AABB). Exact ray-vs-AABB against the SAME volumes
        // the player physically collides with — authored, so trustworthy pick targets. Competes on
        // distance with the baked splat surface below: nearest hit wins.
        const tPrim = this.raycastPrimitiveColliders(originWorld.x, originWorld.y, originWorld.z, nx, ny, nz, dist);
        if (tPrim !== null && tPrim < bestT) bestT = tPrim;

        // Candidate B — generated splat-surface collision: mesh = exact ray–triangle (GLB-baked /
        // LCC2), voxel = coarse sweep. When there's no authored collision, the auto OPACITY-MASS
        // occupancy grid from the splat centers is used (allowSplatGridPick=true): faint airborne
        // floaters never accumulate enough opacity to read as solid, and the sustained march passes
        // through any that slip through, so the reticle lands on the real surface, not mid-air.
        if (this.meshCollision) {
            const tMesh = this.meshCollision.raycast(originWorld.x, originWorld.y, originWorld.z, nx, ny, nz, dist);
            if (tMesh !== null && tMesh < bestT) bestT = tMesh;
        } else {
            const active: SurfaceQuery | null =
                this.voxelCollision ?? (this.allowSplatGridPick ? (this._splatGrid ??= this.buildSplatGrid()) : null);
            if (active) {
                const tSweep = this.sweepSurfaceT(active, originWorld.x, originWorld.y, originWorld.z, nx, ny, nz, dist);
                if (tSweep !== null && tSweep < bestT) bestT = tSweep;
            }
        }

        // Candidate C — precise triangle colliders built from GLB-upload colliders (exact
        // ray–triangle, like the LCC2 mesh; their AABB is skipped in raycastPrimitiveColliders).
        for (const entity of this.collisionEntities) {
            const triangles = triangleColliderOf(entity);
            if (!triangles) continue;
            const tCustom = triangles.raycast(originWorld.x, originWorld.y, originWorld.z, nx, ny, nz, dist);
            if (tCustom !== null && tCustom < bestT) bestT = tCustom;
        }

        return bestT === Infinity
            ? null
            : new pc.Vec3(originWorld.x + nx * bestT, originWorld.y + ny * bestT, originWorld.z + nz * bestT);
    }

    /**
     * Exact ray-vs-AABB against all SOLID primitive colliders (editor boxes/spheres/planes/floor and
     * GLB-upload colliders — each stored as a world-space AABB, the same volume the player collides
     * with). Returns the nearest entry distance t along the normalized ray within maxDist, or null.
     * "Inside"/containment (dome) colliders are skipped — they aren't solid surfaces to move onto.
     */
    private raycastPrimitiveColliders(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): number | null {
        let best: number | null = null;
        for (const entity of this.collisionEntities) {
            const ce = entity as CollisionEntity;
            if (ce._collisionInsideMode) continue; // containment volume, not a solid surface
            if (triangleColliderOf(entity)) continue; // picked precisely via its triangle mesh (candidate C)
            // No precomputed bounds (editor live-add keeps them live for the gizmo): colliderBox
            // derives the world AABB from the WORLD transform, exactly like computePrimitiveBounds.
            const { cx, cy, cz, hx, hy, hz } = colliderBox(entity, this._box);
            const t = rayAabbEntry(ox, oy, oz, dx, dy, dz, cx, cy, cz, hx, hy, hz, maxDist);
            if (t !== null && (best === null || t < best)) best = t;
        }
        return best;
    }

    /**
     * Coarse sphere-sweep → fine point-march → binary refine against a voxel/grid SurfaceQuery
     * (queries run in the splat's LOCAL/file space). Returns the world-space distance t to the
     * surface, or null on a miss. Mesh uses the precise ray–triangle path instead; this serves voxel
     * collision (and the legacy splat grid only when allowSplatGridPick is enabled).
     */
    private sweepSurfaceT(active: SurfaceQuery, ox: number, oy: number, oz: number, nx: number, ny: number, nz: number, dist: number): number | null {
        // fileScale = local-units per world-unit. Voxel/grid queries run in the splat's LOCAL space,
        // so a world-space radius must be scaled into it (else the sweep tunnels when the splat is
        // scaled down / over-covers when scaled up). Same conservative factor as every other
        // file-space query (largest inverse-axis scale): with the step derived from it below, the
        // local spacing between samples never exceeds the local sphere radius on any axis, so a
        // non-uniformly scaled splat can't make the sweep tunnel.
        const fileScale = this.worldSizeToFile(1);

        // Size the coarse sweep to the collision's natural cell resolution so each sphere check spans
        // ~1 cell (O(1)) rather than a far-clip-sized radius. Radius stays ≥ step so overlapping
        // spheres can't tunnel; the iteration cap bounds rays that never hit (e.g. sky).
        const cellLocal = active.queryCellSize?.();
        let coarseStep: number, coarseR: number;
        const maxIters = 16384;
        if (cellLocal && cellLocal > 0) {
            const cellWorld = cellLocal / Math.max(fileScale, 1e-6);
            coarseStep = Math.max(cellWorld, 1e-3);
            coarseR = coarseStep;
        } else {
            coarseStep = Math.max(1e-3, Math.min(dist / 512, 1));
            coarseR = coarseStep * 0.75;
        }

        const sphereSolid = (wx: number, wy: number, wz: number, worldRadius: number): boolean => {
            _rcWorld.set(wx, wy, wz);
            this.worldToFile(_rcWorld, _rcFile);
            return active.checkSphereSolid(_rcFile.x, _rcFile.y, _rcFile.z, worldRadius * fileScale);
        };
        const pointSolid = (wx: number, wy: number, wz: number): boolean => {
            _rcWorld.set(wx, wy, wz);
            this.worldToFile(_rcWorld, _rcFile);
            return active.checkPointSolid(_rcFile.x, _rcFile.y, _rcFile.z);
        };

        const cellAlpha = active.cellAlphaAt
            ? (wx: number, wy: number, wz: number): number => {
                _rcWorld.set(wx, wy, wz);
                this.worldToFile(_rcWorld, _rcFile);
                return active.cellAlphaAt!(_rcFile.x, _rcFile.y, _rcFile.z);
              }
            : null;

        // ── Splat-EXACT pick: when the grid retained per-splat opacity, acceptance runs against the
        //    ACTUAL gaussians (the grid's bricks are pure acceleration) — thin real objects
        //    (railings, plants, props) accept on their true composited alpha instead of
        //    cell-granular mass, and floaters are passed on their true alpha. An exact miss is a
        //    MISS: do NOT fall through to the cell march below (it would re-introduce cell-granular
        //    acceptance). Voxel collision and no-opacity grids keep the marches below. ──
        if (PICK_SPLAT_EXACT && active.supportsExactPick && active.raycastSplatExactLocal) {
            _rcWorld.set(ox, oy, oz);
            this.worldToFile(_rcWorld, _rcFile);
            const lox = _rcFile.x, loy = _rcFile.y, loz = _rcFile.z;
            _rcWorld.set(ox + nx, oy + ny, oz + nz);
            this.worldToFile(_rcWorld, _rcFile);
            let ldx = _rcFile.x - lox, ldy = _rcFile.y - loy, ldz = _rcFile.z - loz;
            const localScale = Math.hypot(ldx, ldy, ldz) || 1;   // local units per world unit along this ray
            ldx /= localScale; ldy /= localScale; ldz /= localScale;
            const tL = active.raycastSplatExactLocal(lox, loy, loz, ldx, ldy, ldz, dist * localScale);
            return tL != null ? tL / localScale : null;
        }

        // ── Voxel / authored solid (no per-cell coverage): keep the exact FIRST-HIT behaviour. ──
        if (!cellAlpha) {
            // Phase 1 — coarse sphere sweep skips empty space without tunnelling (the sphere radius
            // covers the gap between samples).
            let prevT = 0, contactT = -1, iters = 0;
            for (let t = coarseStep; t <= dist && iters < maxIters; t += coarseStep) {
                iters++;
                if (sphereSolid(ox + nx * t, oy + ny * t, oz + nz * t, coarseR)) {
                    contactT = t;
                    break;
                }
                prevT = t;
            }
            if (contactT < 0) return null;

            // Phase 2 — fine point-march from the last empty sample to land on the ACTUAL surface
            // (point-in-solid), not the sphere-contact point ~coarseR in front of it.
            const fineStep = coarseStep / 32;
            const searchEnd = contactT + coarseStep;
            let lastEmpty = prevT, firstSolid = -1;
            for (let t = prevT; t <= searchEnd; t += fineStep) {
                if (pointSolid(ox + nx * t, oy + ny * t, oz + nz * t)) {
                    firstSolid = t;
                    break;
                }
                lastEmpty = t;
            }
            // Grazing / sub-sample-thin contact: the sphere touched solid but no point sample was
            // inside it (silhouette edge). Miss rather than a ~1-cell-in-front mid-air estimate.
            if (firstSolid < 0) return null;

            // Phase 3 — binary-search the surface boundary in [lastEmpty, firstSolid].
            let lo = lastEmpty, hi = firstSolid;
            for (let i = 0; i < 16; i++) {
                const mid = (lo + hi) * 0.5;
                if (pointSolid(ox + nx * mid, oy + ny * mid, oz + nz * mid)) hi = mid;
                else lo = mid;
            }
            return hi;
        }

        // ── Splat grid: SUSTAINED opacity march with α-weighted mean depth. Coarse-skip empty to a
        //    thresholded-solid cluster, then front-to-back alpha-composite inside the contact region.
        //    A run counts as a surface only once its accumulated opacity reaches TAU_HIT — thin/faint
        //    runs (floaters) are passed through entirely, the ray continues to the surface behind.
        //    The returned hit is SuperSplat's depth-pick formulation applied WITHIN the accepted run:
        //    D = Σ t·α·T / Σ α·T, accumulated until the run is ~opaque (T ≤ OPAQUE_T) or ends. That
        //    lands where the surface VISUALLY appears (matches the renderer's own compositing),
        //    sub-cell accurate, and — unlike the old first-nonzero-α front face — insensitive to a
        //    single faint cell touching the run's front edge. NOTE: SuperSplat applies this mean over
        //    the WHOLE ray (fine for their click-to-focus); per-run gating is what keeps it from
        //    averaging a floater with the wall behind it into a mid-air point. ──
        const TAU_HIT = 0.5;                         // accumulated opacity for a run to count as surface
        const OPAQUE_T = 0.05;                       // stop accumulating once transmittance drops here (α ≥ 0.95)
        const fineStep = Math.max(coarseStep * 0.5, 1e-4);
        const cellWorld = coarseStep;                // ≈ 1 cell in world units (Beer–Lambert base length)
        const GAP_TOL = coarseStep;                  // tolerate a 1-cell hole inside one real surface

        // Per-splat gaussian refinement of an ACCEPTED run (fine-grained picks beyond the ~cellSize
        // grid resolution). Transforms the world ray to LOCAL once per accepted run, refines within
        // [runFront, runBack] against the actual gaussians, falls back to the grid mean on null.
        // Refinement never widens the accepted window, so floater gating cannot regress.
        const refineRun = active.refineHitLocal
            ? (runFrontW: number, runBackW: number, gridT: number): number => {
                _rcWorld.set(ox, oy, oz);
                this.worldToFile(_rcWorld, _rcFile);
                const lox = _rcFile.x, loy = _rcFile.y, loz = _rcFile.z;
                _rcWorld.set(ox + nx, oy + ny, oz + nz);
                this.worldToFile(_rcWorld, _rcFile);
                let ldx = _rcFile.x - lox, ldy = _rcFile.y - loy, ldz = _rcFile.z - loz;
                const localScale = Math.hypot(ldx, ldy, ldz) || 1;   // local units per world unit along this ray
                ldx /= localScale; ldy /= localScale; ldz /= localScale;
                const tR = active.refineHitLocal!(lox, loy, loz, ldx, ldy, ldz,
                    runFrontW * localScale, runBackW * localScale, gridT * localScale, 1 / localScale);
                return tR != null ? tR / localScale : gridT;
              }
            : (_f: number, _b: number, gridT: number): number => gridT;

        let searchStart = coarseStep, iters = 0;
        while (searchStart <= dist && iters < maxIters) {
            // coarse skip to the next thresholded-solid contact (faint floaters are invisible here)
            let prevEmpty = Math.max(0, searchStart - coarseStep), contactT = -1;
            for (let t = searchStart; t <= dist && iters < maxIters; t += coarseStep) {
                iters++;
                if (sphereSolid(ox + nx * t, oy + ny * t, oz + nz * t, coarseR)) { contactT = t; break; }
                prevEmpty = t;
            }
            if (contactT < 0) return null;

            // fine sustained accumulation across this contact region
            let inRun = false, runFront = 0, runAccum = 0, trans = 1, gap = 0, lastSolid = contactT;
            let wSum = 0, wtSum = 0;   // α·T weights and α·T-weighted t within the current run
            for (let s = prevEmpty; s <= dist; s += fineStep) {
                const a = cellAlpha(ox + nx * s, oy + ny * s, oz + nz * s);
                if (a > 0) {
                    if (!inRun) { inRun = true; runFront = s; runAccum = 0; trans = 1; wSum = 0; wtSum = 0; }
                    const aStep = 1 - Math.pow(1 - a, fineStep / cellWorld);   // Beer–Lambert resample
                    const w = trans * aStep;
                    runAccum += w; trans *= (1 - aStep); gap = 0; lastSolid = s;
                    wSum += w; wtSum += w * s;
                    // Run is effectively opaque — the visible surface is fully accumulated.
                    if (trans <= OPAQUE_T) return refineRun(runFront, s, wtSum / wSum);
                } else if (inRun) {
                    gap += fineStep;
                    if (gap > GAP_TOL) {
                        // Run ended. Accepted (real surface) → per-splat-refined α-weighted mean depth;
                        // else it was a faint blob → pass through and resume the coarse skip behind it.
                        if (runAccum >= TAU_HIT) return refineRun(runFront, lastSolid, wtSum / wSum);
                        inRun = false; break;
                    }
                }
            }
            // Ray/limit ended while still inside a run — accept it if it accumulated enough.
            if (inRun && runAccum >= TAU_HIT && wSum > 0) return refineRun(runFront, lastSolid, wtSum / wSum);
            searchStart = lastSolid + coarseStep;                  // continue past the exhausted run
        }
        return null;
    }

    /**
     * Check ground below player
     */
    private checkGround(position: pc.Vec3): number | null {
        const feetY = position.y - this.playerHeight;
        const maxReachableGroundY = feetY + this.stepHeight;
        let highestGround: number | null = null;
        const considerGround = (groundY: number) => {
            if (groundY > maxReachableGroundY) return;
            if (highestGround === null || groundY > highestGround) {
                highestGround = groundY;
            }
        };

        // Check against floor entity if exists
        if (this.floorEntity) {
            const floorPos = this.floorEntity.getPosition();
            const floorScale = this.floorEntity.getLocalScale();
            // Check if we're above the floor plane
            const halfWidth = floorScale.x / 2;
            const halfDepth = floorScale.z / 2;
            if (Math.abs(position.x - floorPos.x) < halfWidth &&
                Math.abs(position.z - floorPos.z) < halfDepth) {
                considerGround(floorPos.y);
            }
        }
        // Check against other collision meshes for ground
        const box = this._box;
        for (const entity of this.collisionEntities) {
            const meshType = (entity as CollisionEntity)._collisionMeshType;
            // Skip floor/plane (handled above) and sphere/torus (curved tops, not good for walking on)
            if (meshType === 'floor' || meshType === 'plane' || meshType === 'sphere' || meshType === 'torus') {
                continue;
            }
            const triangles = triangleColliderOf(entity);
            if (triangles) {
                // Stand on its REAL surface, matching the precise picking, not the bounding-box top.
                const groundY = triangles.findGround(position.x, position.z, feetY + this.stepHeight);
                if (groundY !== null) considerGround(groundY);
                continue;
            }
            colliderBox(entity, box);
            // Check if we're above this mesh
            if (Math.abs(position.x - box.cx) < box.hx + this.collisionRadius &&
                Math.abs(position.z - box.cz) < box.hz + this.collisionRadius) {
                considerGround(box.cy + box.hy);
            }
        }
        // Splat-derived ground check (voxel or triangle-mesh).
        // Voxel data lives in file space, so we round-trip through worldToFile.
        // Mesh data is pre-baked into world space (see initMeshCollision /
        // setSplatEntity), so we query directly without any transform.
        const activeForGround = this.activeCollision;
        if (activeForGround) {
            const isMeshGround = this.meshCollision !== null && activeForGround === this.meshCollision;
            if (isMeshGround) {
                const groundY = activeForGround.findGround(position.x, position.z, feetY + this.stepHeight);
                if (groundY !== null) considerGround(groundY);
            } else {
                // World-metre sizes stay world-metre: the feet are found in WORLD space
                // (eye − playerHeight) before the transform, the step allowance is converted to
                // file units (fileStepHeight), and the hit comes back as a world Y — so standing
                // height and step-up don't change with the splat's scale.
                const feetWorldPos = this.tmpVec;
                const fileFeetPos = this.tmpVec2;
                feetWorldPos.set(position.x, feetY, position.z);
                this.worldToFile(feetWorldPos, fileFeetPos);
                const yPerFileY = this.worldYPerFileY();
                const stepHeight = this.fileStepHeight();
                const fileGroundY = yPerFileY < 0
                    ? activeForGround.findGroundInvertedY(fileFeetPos.x, fileFeetPos.z, fileFeetPos.y - stepHeight)
                    : activeForGround.findGround(fileFeetPos.x, fileFeetPos.z, fileFeetPos.y + stepHeight);
                if (fileGroundY !== null) {
                    // Transform file-space ground point back to world Y
                    const groundFilePoint = this.tmpVec3;
                    groundFilePoint.set(fileFeetPos.x, fileGroundY, fileFeetPos.z);
                    const m = this._splatEntity ? this._splatMatrix.data : null;
                    let worldGroundY: number;
                    if (m) {
                        // Only need the Y component of splatMatrix * groundFilePoint
                        worldGroundY = m[1] * groundFilePoint.x + m[5] * groundFilePoint.y + m[9] * groundFilePoint.z + m[13];
                    } else {
                        worldGroundY = -fileGroundY; // fallback: default (1,-1,-1)
                    }
                    considerGround(worldGroundY);
                }
            }
        }
        return highestGround;
    }

    private snapToGroundIfClose(): void {
        const pos = this.camera.getPosition().clone();
        const groundY = this.checkGround(pos);
        if (groundY !== null) {
            const feetY = pos.y - this.playerHeight;
            if (feetY <= groundY + this.groundCheckDistance) {
                pos.y = groundY + this.playerHeight;
                this.camera.setPosition(pos);
                this.velocity.y = 0;
                this.isGrounded = true;
                this.wasGroundedLastFrame = true;
                this.lastGroundedTime = this.currentTime;
            }
            return;
        }

        // No ground within stepHeight — but on walk-mode entry the camera
        // is often nowhere near the surface (e.g. orbit cam above the roof,
        // or below the LCC2 floor mesh after a fresh import). Do a wider scan
        // so we teleport onto the nearest floor instead of falling forever.
        const rescueGroundY = this.findRescueGround(pos);
        if (rescueGroundY !== null) {
            pos.y = rescueGroundY + this.playerHeight;
            this.camera.setPosition(pos);
            this.velocity.y = 0;
            this.isGrounded = true;
            this.wasGroundedLastFrame = true;
            this.lastGroundedTime = this.currentTime;
            console.log(`[CharacterController] Walk-mode entry rescue: snapped feet to y=${rescueGroundY.toFixed(2)}`);
            return;
        }

        this.isGrounded = false;
    }

    /**
     * Wider-range ground search used only on walk-mode entry, when the camera
     * may be far from any surface. Scans both downward (without the stepHeight
     * cap that {@link checkGround} enforces) and upward — picks the floor
     * closest to the player's current Y. Returns null only if there is no
     * collision data with a floor under or above (x, z).
     */
    private findRescueGround(position: pc.Vec3): number | null {
        let best: number | null = null;
        const consider = (groundY: number) => {
            if (best === null || Math.abs(groundY - position.y) < Math.abs(best - position.y)) {
                best = groundY;
            }
        };

        // Primitive floor / collision meshes (no stepHeight cap).
        if (this.floorEntity) {
            const fp = this.floorEntity.getPosition();
            const fs = this.floorEntity.getLocalScale();
            if (Math.abs(position.x - fp.x) < fs.x / 2 && Math.abs(position.z - fp.z) < fs.z / 2) {
                consider(fp.y);
            }
        }
        for (const entity of this.collisionEntities) {
            const meshType = (entity as CollisionEntity)._collisionMeshType;
            if (meshType === 'floor' || meshType === 'plane' || meshType === 'sphere' || meshType === 'torus') continue;
            const triangles = triangleColliderOf(entity);
            if (triangles) {
                // Its real surface below or above, not the bounding-box top (a wall's or roof's).
                const down = triangles.findGround(position.x, position.z, position.y);
                if (down !== null) consider(down);
                const up = triangles.findGroundInvertedY(position.x, position.z, position.y);
                if (up !== null) consider(up);
                continue;
            }
            const { cx, cy, cz, hx, hy, hz } = colliderBox(entity, this._box);
            if (Math.abs(position.x - cx) < hx + this.collisionRadius &&
                Math.abs(position.z - cz) < hz + this.collisionRadius) {
                consider(cy + hy);
            }
        }

        // Splat-derived collision: cast both directions and consider both hits.
        const active = this.activeCollision;
        if (active) {
            const isMesh = this.meshCollision !== null && active === this.meshCollision;
            if (isMesh) {
                const down = active.findGround(position.x, position.z);
                if (down !== null) consider(down);
                const up = active.findGroundInvertedY(position.x, position.z, position.y);
                if (up !== null) consider(up);
            } else {
                // Voxel: query in file space, transform results back to world Y.
                const fileFeet = this.tmpVec2;
                const worldFeet = this.tmpVec;
                worldFeet.set(position.x, position.y, position.z);
                this.worldToFile(worldFeet, fileFeet);
                const yPerFileY = this.worldYPerFileY();
                const m = this._splatEntity ? this._splatMatrix.data : null;
                const fileToWorldY = (filePoint: pc.Vec3): number => {
                    if (m) return m[1] * filePoint.x + m[5] * filePoint.y + m[9] * filePoint.z + m[13];
                    return -filePoint.y; // (1,-1,-1) fallback
                };
                const down = yPerFileY < 0
                    ? active.findGroundInvertedY(fileFeet.x, fileFeet.z, fileFeet.y)
                    : active.findGround(fileFeet.x, fileFeet.z);
                if (down !== null) {
                    const p = this.tmpVec3;
                    p.set(fileFeet.x, down, fileFeet.z);
                    consider(fileToWorldY(p));
                }
                const up = yPerFileY < 0
                    ? active.findGround(fileFeet.x, fileFeet.z)
                    : active.findGroundInvertedY(fileFeet.x, fileFeet.z, fileFeet.y);
                if (up !== null) {
                    const p = this.tmpVec3;
                    p.set(fileFeet.x, up, fileFeet.z);
                    consider(fileToWorldY(p));
                }
            }
        }

        return best;
    }

    /**
     * Update the character controller - call this every frame
     */
    update(dt: number): void {
        if (!this.enabled)
            return;
        // Update splat transform matrices for voxel queries
        this.updateSplatTransform();
        // 1. Increment timer
        this.currentTime += dt;
        // 1b. Game controller (gamepad) input — walk with a physical controller.
        // Left stick moves, right stick looks (continuous, deg/s), A jumps,
        // right bumper / right trigger / left-stick-click sprints.
        const gp = this.gamepadEnabled ? readGamepad() : null;
        let gpMoveX = 0, gpMoveZ = 0, gpSprint = false;
        if (gp && gp.connected) {
            gpMoveX = gp.leftX;
            gpMoveZ = gp.leftY; // stick up = forward
            // Right-stick look: right = turn right, up = look up (matches the
            // mouse-look sign; invertCameraRotation flips only the pitch axis).
            if (gp.rightX !== 0 || gp.rightY !== 0) {
                this.yaw -= gp.rightX * this.gamepadLookSpeed * dt;
                this.pitch -= (this.invertCameraRotation ? -gp.rightY : gp.rightY) * this.gamepadLookSpeed * dt;
                this.pitch = Math.max(-89, Math.min(89, this.pitch));
                // A deliberate look cancels auto-move / walk-to, like mouse-look.
                if (this.autoMoveForward) this.autoMoveForward = false;
                if (this._walkTarget) this._walkTarget = null;
            }
            // Jump on A rising edge (feeds the same coyote/jump-buffer logic as Space).
            const jumpNow = gp.buttons[GP_BUTTON.A] ?? false;
            if (jumpNow && !this.gpJumpHeld) this.lastJumpPressTime = this.currentTime;
            // Variable jump height: cut upward velocity on early release, like Space.
            if (!jumpNow && this.gpJumpHeld && this.velocity.y > 0) this.velocity.y *= 0.5;
            this.gpJumpHeld = jumpNow;
            gpSprint = (gp.buttons[GP_BUTTON.RB] ?? false) || gp.rt > 0.5 || (gp.buttons[GP_BUTTON.L3] ?? false);
        }
        // 2. Apply mobile joystick look input (yaw/pitch)
        // Joystick look is continuous (held position = constant rotation speed)
        // Not consumed per-frame — stays at last set value until released (set to 0)
        if (this.joystickLookX !== 0 || this.joystickLookY !== 0) {
            const lookSpeed = 120; // degrees per second at full deflection
            const lookSign = lookAxisSign(this.invertCameraRotation);
            this.yaw -= this.joystickLookX * lookSign * lookSpeed * dt;
            this.pitch -= this.joystickLookY * lookSign * lookSpeed * dt;
            this.pitch = Math.max(-89, Math.min(89, this.pitch));
        }
        // 3. Get movement input (keyboard + joystick combined, clamped to -1..1)
        let moveX = (this.keys['KeyD'] || this.keys['ArrowRight'] ? 1 : 0) -
            (this.keys['KeyA'] || this.keys['ArrowLeft'] ? 1 : 0);
        let moveZ = (this.keys['KeyW'] || this.keys['ArrowUp'] ? 1 : 0) -
            (this.keys['KeyS'] || this.keys['ArrowDown'] ? 1 : 0);
        // Blend in joystick + gamepad input
        moveX = Math.max(-1, Math.min(1, moveX + this.joystickMoveX + gpMoveX));
        moveZ = Math.max(-1, Math.min(1, moveZ + this.joystickMoveZ + gpMoveZ));
        // Auto-move: inject forward input at reduced speed when no manual input
        if (this.autoMoveForward && moveX === 0 && moveZ === 0) {
            moveZ = this.autoMoveSpeedFactor;
        }
        // Cancel walk-to-target on any manual movement input
        if (this._walkTarget && (moveX !== 0 || moveZ !== 0)) {
            this._walkTarget = null;
        }
        // Walk-to-target: override movement direction toward target
        if (this._walkTarget) {
            const camPos = this.camera.getPosition();
            const dx = this._walkTarget.x - camPos.x;
            const dz = this._walkTarget.z - camPos.z;
            const dist = Math.sqrt(dx * dx + dz * dz);
            if (dist < this._walkTargetArrivalDist) {
                this._walkTarget = null;
            } else {
                // Compute yaw toward target and rotate smoothly
                // forward = (-sin(yaw), 0, -cos(yaw)), so yaw = atan2(-forward.x, -forward.z)
                // Here direction to target is (dx, 0, dz), so targetYaw = atan2(-dx, -dz)
                const targetYaw = Math.atan2(-dx, -dz) * (180 / Math.PI);
                // Shortest angle delta
                let deltaYaw = targetYaw - this.yaw;
                if (deltaYaw > 180) deltaYaw -= 360;
                if (deltaYaw < -180) deltaYaw += 360;
                // Smooth turn — gradual enough for a natural walking arc
                const absDelta = Math.abs(deltaYaw);
                // Ease-out: turn faster when far from target, slower as we approach
                // Base rate ~120°/s, scales up to ~200°/s for large angles
                const turnRate = 120 + 80 * Math.min(absDelta / 90, 1);
                const turnSpeed = turnRate * dt;
                this.yaw += Math.max(-turnSpeed, Math.min(turnSpeed, deltaYaw));
                // Walk forward
                moveZ = 1;
                moveX = 0;
            }
        }
        const isSprinting = this.keys['ShiftLeft'] || this.keys['ShiftRight'] || gpSprint;
        // Calculate forward and right vectors (horizontal only)
        const yawRad = this.yaw * (Math.PI / 180);
        this.forward.set(-Math.sin(yawRad), 0, -Math.cos(yawRad));
        this.right.set(Math.cos(yawRad), 0, -Math.sin(yawRad));
        // Calculate target velocity based on input
        const speed = this.moveSpeed * (isSprinting ? this.sprintMultiplier : 1);
        this.targetVelocity.set(0, 0, 0);
        this.targetVelocity.add(this.tmpVec2.copy(this.forward).mulScalar(moveZ * speed));
        this.targetVelocity.add(this.tmpVec2.copy(this.right).mulScalar(moveX * speed));
        // 3. Smooth the horizontal velocity toward target (creates ease-in/ease-out)
        const lerpFactor = damp(this.moveDamping, dt);
        this.horizontalVelocity.lerp(this.horizontalVelocity, this.targetVelocity, lerpFactor);
        // 4. Apply gravity (vertical velocity is not smoothed for natural feel)
        if (!this.isGrounded) {
            this.velocity.y -= this.gravity * dt;
            this.velocity.y = Math.max(-this.maxFallSpeed, this.velocity.y);
        }
        // 5. Get current position (subtract previous bob/dip offsets to get physics position)
        const currentPos = this.camera.getPosition().clone();
        currentPos.y -= this.bobOffsetY - this.landingDipAmount;
        currentPos.x -= this.bobOffsetX * this.right.x;
        currentPos.z -= this.bobOffsetX * this.right.z;
        // 5b. Voxel rescue: a jump since the last frame (teleport, waypoint jump, a host moving the
        // camera) is a new placement, checked like a spawn (resolveVoxelPlacement).
        if (this._hasLastPhysicsPos && currentPos.distance(this._lastPhysicsPos) > CharacterController.TELEPORT_DISTANCE) {
            this._voxelPlacementPending = true;
        }
        if (this._voxelPlacementPending) this.resolveVoxelPlacement(currentPos);
        // Calculate new position using smoothed horizontal velocity
        const newPos = new pc.Vec3();
        newPos.x = currentPos.x + this.horizontalVelocity.x * dt;
        newPos.y = currentPos.y + this.velocity.y * dt;
        newPos.z = currentPos.z + this.horizontalVelocity.z * dt;
        // 6. Check horizontal collision (X axis) — primitives only
        this.tmpVec2.set(newPos.x, currentPos.y, currentPos.z);
        if (this.checkCollision(this.tmpVec2, this.collisionRadius)) {
            newPos.x = currentPos.x;
        }
        // Check horizontal collision (Z axis) — primitives only
        this.tmpVec2.set(newPos.x, currentPos.y, newPos.z);
        if (this.checkCollision(this.tmpVec2, this.collisionRadius)) {
            newPos.z = currentPos.z;
        }
        // 6b. Voxel collision — horizontal push-out only. Vertical placement is
        // handled by the feet-based ground check below; applying voxel Y push-out
        // from an eye-height sphere can eject the player onto overhead geometry.
        this.resolveVoxelCollision(newPos, this.collisionRadius, false);
        // 6c. A voxel gap narrower than the player (a doorway too tight for its width) stops it
        // where it stood, like a wall — the push-out alone would squeeze it through sideways.
        // Not when it already stood wedged (spawned or teleported in): then it may move out.
        if (this.isVoxelWedged(newPos, this.collisionRadius) && !this.isVoxelWedged(currentPos, this.collisionRadius)) {
            newPos.x = currentPos.x;
            newPos.z = currentPos.z;
        }
        // 6d. Boxed in by voxels (stray voxels around a spawn or teleport target): pressing to move
        // but not getting anywhere, with no way out the body fits through -> walk through voxels
        // until out. Pressing against an ordinary wall with open space behind changes nothing.
        this.updateVoxelTrap(currentPos, newPos, moveX !== 0 || moveZ !== 0, dt);
        // 7. Check ground
        const wasGrounded = this.wasGroundedLastFrame;
        const groundY = this.checkGround(newPos);
        const targetFeetY = newPos.y - this.playerHeight;
        if (groundY !== null && targetFeetY <= groundY + this.groundCheckDistance) {
            // On ground
            newPos.y = groundY + this.playerHeight;
            this.velocity.y = 0;
            this.isGrounded = true;
        }
        else if (groundY === null || targetFeetY > groundY + this.stepHeight) {
            // In air
            this.isGrounded = false;
        }
        // Update coyote time tracking
        if (this.isGrounded) {
            this.lastGroundedTime = this.currentTime;
        }
        // 8. Coyote time + jump buffer check
        const withinCoyote = (this.currentTime - this.lastGroundedTime) <= this.coyoteTime;
        const withinBuffer = (this.currentTime - this.lastJumpPressTime) <= this.jumpBufferTime;
        if (withinCoyote && withinBuffer) {
            this.velocity.y = this.jumpVelocity;
            this.isGrounded = false;
            this.lastGroundedTime = -Infinity;   // consume coyote
            this.lastJumpPressTime = -Infinity;  // consume buffer
        }
        // 9. Landing camera dip
        if (this.isGrounded && !wasGrounded) {
            // Just landed — dip proportional to impact velocity
            this.landingDipAmount = Math.min(
                Math.abs(this.previousVelocityY) * 0.02,
                CharacterController.MAX_LANDING_DIP
            );
        }
        // Decay landing dip
        this.landingDipAmount *= 1 - Math.min(1, dt * CharacterController.LANDING_DIP_DECAY);
        if (this.landingDipAmount < 0.001) this.landingDipAmount = 0;
        // 10. Head bob (visual-only offset)
        const horizSpeed = Math.sqrt(
            this.horizontalVelocity.x * this.horizontalVelocity.x +
            this.horizontalVelocity.z * this.horizontalVelocity.z
        );
        const isMoving = this.isGrounded && horizSpeed > 0.5;
        if (this.headBobEnabled && isMoving) {
            const bobFreq = isSprinting ? CharacterController.SPRINT_BOB_FREQ : CharacterController.WALK_BOB_FREQ;
            const ampY = isSprinting ? CharacterController.SPRINT_BOB_AMP_Y : CharacterController.WALK_BOB_AMP_Y;
            const ampX = isSprinting ? CharacterController.SPRINT_BOB_AMP_X : CharacterController.WALK_BOB_AMP_X;
            this.bobTimer += dt * bobFreq;
            this.bobOffsetY = Math.sin(this.bobTimer) * ampY;
            this.bobOffsetX = Math.cos(this.bobTimer * 0.5) * ampX;
        } else {
            // Decay bob offsets smoothly to zero
            const decayFactor = 1 - Math.min(1, dt * 8);
            this.bobOffsetY *= decayFactor;
            this.bobOffsetX *= decayFactor;
            if (Math.abs(this.bobOffsetY) < 0.0001) this.bobOffsetY = 0;
            if (Math.abs(this.bobOffsetX) < 0.0001) this.bobOffsetX = 0;
            // Reset timer when stopped so bob always starts from a consistent phase
            if (this.bobOffsetY === 0 && this.bobOffsetX === 0) {
                this.bobTimer = 0;
            }
        }
        // 11. Apply camera position = physics pos + bob offsets - landing dip
        // Containment: keep the player inside any "inside"-mode colliders (e.g. a
        // dome), in all axes — including flying up out of the top.
        this.clampToInsideColliders(newPos);
        const camX = newPos.x + this.bobOffsetX * this.right.x;
        const camY = newPos.y + this.bobOffsetY - this.landingDipAmount;
        const camZ = newPos.z + this.bobOffsetX * this.right.z;
        this.camera.setPosition(camX, camY, camZ);
        // Apply rotation
        this.camera.setEulerAngles(this.pitch, this.yaw, 0);
        // 12. Store state for next frame
        this.previousVelocityY = this.velocity.y;
        this.wasGroundedLastFrame = this.isGrounded;
        this._lastPhysicsPos.copy(newPos);
        this._hasLastPhysicsPos = true;
    }

    // -- Voxel rescue (see voxelRescue.ts) --------------------------------------------------------

    /** True when splat collision is on and the voxel set (not the mesh) is what is queried. */
    private isVoxelCollisionActive(): boolean {
        const active = this.activeCollision;
        return !!active && !(this.meshCollision !== null && active === this.meshCollision);
    }

    /**
     * Whether the walk body (the eye-height sphere the voxel push-out resolves) at world (x, y, z)
     * is held by voxels: solid voxels within the body's radius around the eyes, in a horizontal
     * band reaching about a third of the radius above and below them. Voxels that only reach into the sphere from above or
     * below (a low ceiling, a beam just over the eyes) push the walker vertically, which walking
     * ignores, so they do not count: under them the walker walks freely, and a wall it presses
     * against there stays a wall. A body resting against a wall (the push-out leaves it exactly
     * one radius away) counts as free: the test radius is 5% smaller.
     *
     * The band is sampled in WORLD space (a centre sphere and a ring of RESCUE_BAND_RING small
     * spheres), so it stays horizontal for a rotated splat; each is an octree-pruned sphere test,
     * and only bodies that touch voxels at all get that far.
     */
    private voxelBodyOverlaps(x: number, y: number, z: number): boolean {
        const active = this.activeCollision;
        if (!active || (this.meshCollision !== null && active === this.meshCollision)) return false;
        const radius = this.collisionRadius * 0.95;
        if (!this.voxelSphereSolidAt(active, x, y, z, radius)) return false;
        const band = radius * CharacterController.RESCUE_BAND_FRACTION;
        if (this.voxelSphereSolidAt(active, x, y, z, band)) return true;
        const ring = radius - band;
        const count = CharacterController.RESCUE_BAND_RING;
        for (let i = 0; i < count; i++) {
            const angle = (i / count) * Math.PI * 2;
            if (this.voxelSphereSolidAt(active, x + Math.sin(angle) * ring, y, z + Math.cos(angle) * ring, band)) return true;
        }
        return false;
    }

    /** One voxel sphere test at a world position and world radius. */
    private voxelSphereSolidAt(active: ActiveCollision, x: number, y: number, z: number, worldRadius: number): boolean {
        const file = this.worldToFile(this._rescueProbe.set(x, y, z), this._rescueFilePos);
        return active.checkSphereSolid(file.x, file.y, file.z, this.worldSizeToFile(worldRadius));
    }

    /** Sample spacing of the rescue searches: half the body radius, within [0.05, 0.25] m. */
    private rescueStep(): number {
        return Math.min(0.25, Math.max(0.05, this.collisionRadius * 0.5));
    }

    /** No way out of here (within RESCUE_SEARCH_RADIUS) that the body fits through. */
    private isBoxedInByVoxels(pos: pc.Vec3): boolean {
        const y = pos.y;
        return isEnclosed(pos.x, pos.z, (x, z) => !this.voxelBodyOverlaps(x, y, z),
            this.rescueStep(), CharacterController.RESCUE_SEARCH_RADIUS);
    }

    /**
     * A placement (walk-mode entry, teleport, a newly activated voxel set): a body embedded in
     * voxels moves to the nearest free spot; with none in reach, or when that spot is boxed in,
     * the body may walk through voxels until it is out (updateVoxelTrap ends that). Stays pending
     * until a voxel set is active, so a set that finishes loading later is checked too.
     */
    private resolveVoxelPlacement(pos: pc.Vec3): void {
        if (!this.isVoxelCollisionActive()) return;
        this._voxelPlacementPending = false;
        this._voxelBlockedTime = 0;
        this._voxelTrapChecks = 0;
        this._voxelEscape = false;
        this.updateSplatTransform();
        if (this.voxelBodyOverlaps(pos.x, pos.y, pos.z)) {
            const y = pos.y;
            // Nearest free spot, preferring the way the walker faces (past a clump, not behind it).
            const yawRad = this.yaw * (Math.PI / 180);
            const spot = findNearestFreeSpot(pos.x, pos.z, (x, z) => !this.voxelBodyOverlaps(x, y, z),
                this.rescueStep(), CharacterController.RESCUE_SEARCH_RADIUS, { x: -Math.sin(yawRad), z: -Math.cos(yawRad) });
            if (!spot) {
                this._voxelEscape = true;
                this._voxelEscapeCheckTime = this.currentTime;
                console.log('[CharacterController] Started inside voxels with no free spot nearby: walking out through them');
                return;
            }
            console.log(`[CharacterController] Started inside voxels: moved ${Math.hypot(spot.x - pos.x, spot.z - pos.z).toFixed(2)} m to a free spot`);
            pos.x = spot.x;
            pos.z = spot.z;
        }
        if (this.isBoxedInByVoxels(pos)) {
            this._voxelEscape = true;
            this._voxelEscapeCheckTime = this.currentTime;
            console.log('[CharacterController] Started boxed in by voxels: walking out through them');
        }
    }

    /**
     * Boxed-in detection while walking, and the end of an escape. Pressing to move for
     * TRAP_BLOCKED_SECONDS while the body (almost) does not move runs the boxed-in check once;
     * only a body with no way out turns escape on (an ordinary wall changes nothing). Escape ends
     * once the body overlaps no voxels and is no longer boxed in.
     */
    private updateVoxelTrap(from: pc.Vec3, to: pc.Vec3, hasInput: boolean, dt: number): void {
        if (!this.isVoxelCollisionActive()) {
            this._voxelEscape = false;
            this._voxelBlockedTime = 0;
            return;
        }
        if (this._voxelEscape) {
            if (this.currentTime - this._voxelEscapeCheckTime >= CharacterController.ESCAPE_RECHECK_SECONDS
                && !this.voxelBodyOverlaps(to.x, to.y, to.z)) {
                this._voxelEscapeCheckTime = this.currentTime;
                if (!this.isBoxedInByVoxels(to)) {
                    this._voxelEscape = false;
                    console.log('[CharacterController] Out of the voxels: voxel collision is back on');
                }
            }
            return;
        }
        const intended = Math.hypot(this.horizontalVelocity.x, this.horizontalVelocity.z) * dt;
        const moved = Math.hypot(to.x - from.x, to.z - from.z);
        if (!hasInput || !(intended > 1e-4)) {
            this._voxelBlockedTime = 0;
            return;
        }
        if (moved >= intended * 0.25) {
            this._voxelBlockedTime = 0;
            this._voxelTrapChecks = 0;
            return;
        }
        this._voxelBlockedTime += dt;
        // Each check that finds a way out doubles the wait before the next (pressing against an
        // ordinary wall costs one flood fill every few seconds at most, not one a second).
        const wait = Math.min(CharacterController.TRAP_MAX_WAIT_SECONDS,
            CharacterController.TRAP_BLOCKED_SECONDS * 2 ** this._voxelTrapChecks);
        if (this._voxelBlockedTime < wait) return;
        this._voxelBlockedTime = 0;
        if (this.isBoxedInByVoxels(from)) {
            this._voxelEscape = true;
            this._voxelEscapeCheckTime = this.currentTime;
            this._voxelTrapChecks = 0;
            console.log('[CharacterController] Boxed in by voxels: walking out through them');
        } else {
            this._voxelTrapChecks++;
        }
    }
    /**
     * Clean up resources
     */
    destroy(): void {
        this.disable();
        this._voxelLoadToken++;
        this._meshLoadToken++;
        // Remove collision entities
        for (const entity of this.collisionEntities) {
            entity.destroy();
        }
        this.collisionEntities = [];
        this._sceneCollidersToken++;
        this.sceneColliders.clear();
        this.floorEntity = null;
        this.voxelCollision = null;
        this.meshCollision = null;
        this._voxelCache.clear();
        this._meshCache.clear();
        this.clearVoxelDebug();
    }
    /**
     * Initialize voxel octree collision from a .voxel.json URL.
     * Non-fatal: logs warning on failure, primitive collision still works.
     */
    async initVoxelCollision(jsonUrl: string): Promise<void> {
        // Bump the token BEFORE the already-active check: every request, including one for the
        // set that is already active, supersedes a load still in flight. Otherwise returning to
        // a splat whose set is active (e.g. back to the primary while a swap's own set is still
        // downloading) left the older load's token current, and it installed the wrong set later.
        const loadToken = ++this._voxelLoadToken;
        // Skip if already loaded with this exact URL
        if (jsonUrl === this._lastVoxelUrl && this.voxelCollision) return;
        const cached = this._voxelCache.get(jsonUrl);
        if (cached) {
            this.clearVoxelDebug();
            this.voxelCollision = cached;
            this._lastVoxelUrl = jsonUrl;
            this._voxelPlacementPending = true; // the player may stand inside the new set
            return;
        }
        try {
            console.log('[CharacterController] Loading voxel collision:', jsonUrl);
            const voxel = await VoxelCollision.load(jsonUrl);
            if (loadToken !== this._voxelLoadToken) return;
            this._voxelCache.set(jsonUrl, voxel);
            this.clearVoxelDebug();
            this.voxelCollision = voxel;
            this._lastVoxelUrl = jsonUrl;
            this._voxelPlacementPending = true; // the player may stand inside the new set
            console.log('[CharacterController] Voxel collision ready');
        } catch (err) {
            if (loadToken !== this._voxelLoadToken) return;
            console.warn('[CharacterController] Voxel collision load failed (falling back to primitives):', err);
            this.voxelCollision = null;
            this._lastVoxelUrl = null;
            this.clearVoxelDebug();
        }
    }
    /** Drop the loaded voxel collision so the active query falls back to mesh/none. */
    clearVoxelCollision(): void {
        this.clearVoxelCollisionCache();
        this.detachVoxelCollision();
    }
    /** Forget cached voxel sets (a regenerated one may come back under the same URL); the active set stays. */
    clearVoxelCollisionCache(): void {
        this._voxelCache.clear();
    }
    /** Stop querying the voxel collision but keep it cached for a later initVoxelCollision. */
    detachVoxelCollision(): void {
        this._voxelLoadToken++;
        this.voxelCollision = null;
        this._lastVoxelUrl = null;
        this.clearVoxelDebug();
    }
    /**
     * Initialize triangle-mesh collision from a .collision.glb URL.
     * Loaded data lives alongside any voxel data — `setCollisionSystem` chooses
     * which one is queried.
     */
    async initMeshCollision(glbUrl: string): Promise<void> {
        // Token first, so a same-URL request still supersedes an in-flight load (see initVoxelCollision).
        const loadToken = ++this._meshLoadToken;
        if (glbUrl === this._lastMeshUrl && this.meshCollision) return;
        const cached = this._meshCache.get(glbUrl);
        if (cached) {
            this.meshCollision = cached;
            this.refreshSplatCollisionTransform();
            this._lastMeshUrl = glbUrl;
            return;
        }
        try {
            console.log('[CharacterController] Loading mesh collision:', glbUrl);
            const mesh = await MeshCollision.load(glbUrl);
            // Race guard: if a later setCollisionMeshUrl / clear / destroy fired
            // while we were awaiting, drop this result. Without this, two rapid
            // calls (or a load resolving after destroy()) could stomp later state
            // or write into a destroyed controller.
            if (loadToken !== this._meshLoadToken) return;
            this._meshCache.set(glbUrl, mesh);
            this.meshCollision = mesh;
            // Bake the splat entity's current world matrix into the mesh so queries
            // can run in world space — the XZ grid + Y-down ground rays would
            // otherwise misread Z-up source data (e.g. LCC2 with the [90,90,0]
            // viewer-side coordinate correction).
            this.refreshSplatCollisionTransform();
            this._lastMeshUrl = glbUrl;
            console.log('[CharacterController] Mesh collision ready');
        } catch (err) {
            if (loadToken !== this._meshLoadToken) return;
            // Loud — a swallowed load here disables all splat collision (walk
            // *and* fly/orbit) without any visible symptom. Most common cause
            // is exceeding MeshCollision's MAX_TRIANGLES / MAX_GLB_BYTES caps.
            console.error('[CharacterController] Mesh collision load failed — walk/fly/orbit will have NO splat collision:', err);
            this.meshCollision = null;
            this._lastMeshUrl = null;
        }
    }
    /** Drop the loaded mesh collision so the active query falls back to voxel/none. */
    clearMeshCollision(): void {
        this.clearMeshCollisionCache();
        this.detachMeshCollision();
    }
    /** Forget cached mesh sets (a regenerated one may come back under the same URL); the active set stays. */
    clearMeshCollisionCache(): void {
        this._meshCache.clear();
    }
    /** Stop querying the mesh collision but keep it cached for a later initMeshCollision. */
    detachMeshCollision(): void {
        this._meshLoadToken++;
        this.meshCollision = null;
        this._lastMeshUrl = null;
    }
    /** Switch which loaded collision representation is queried at runtime. */
    setCollisionSystem(system: 'voxel' | 'mesh'): void {
        if (this.collisionSystem === system) return;
        this.collisionSystem = system;
        this._voxelPlacementPending = true; // switching to voxels may put the player inside them
        console.log('[CharacterController] Active collision system →', system);
    }
    /** Get the active collision system selector ('voxel' or 'mesh'). */
    getCollisionSystem(): 'voxel' | 'mesh' {
        return this.collisionSystem;
    }
    /** Get the representation actually used after availability and enable-state fallback. */
    getActiveCollisionSystem(): 'voxel' | 'mesh' | null {
        const active = this.activeCollision;
        if (active && this.voxelCollision && active === this.voxelCollision) return 'voxel';
        if (active && this.meshCollision && active === this.meshCollision) return 'mesh';
        return null;
    }
    /** Enable or disable splat-derived collision queries (voxel + mesh). Custom primitives are unaffected. */
    setSplatCollisionEnabled(enabled: boolean): void {
        if (this.splatCollisionEnabled === enabled) return;
        this.splatCollisionEnabled = enabled;
        if (enabled) this._voxelPlacementPending = true; // the player may stand inside the voxels
        // Force a fresh ground re-check next frame. Without this, a player that
        // was standing on splat-derived ground keeps `isGrounded = true` for
        // one frame after the toggle flips off, which lets jump+gravity skip a
        // tick and looks like a hiccup. Mirrors CameraControls' invalidation
        // of `_prevPositionValid` on the same toggle.
        this.isGrounded = false;
        console.log('[CharacterController] Splat collision enabled →', enabled);
    }
    /** Whether splat-derived collision queries are currently being applied. */
    getSplatCollisionEnabled(): boolean {
        return this.splatCollisionEnabled;
    }
    /** Whether the mesh-collision GLB is currently loaded. */
    hasMeshCollision(): boolean {
        return this.meshCollision !== null;
    }
    /** Set the splat entity so voxel queries can transform world↔file coordinates */
    setSplatEntity(entity: pc.Entity | null): void {
        // Another splat's collision (a splat swap) is a placement: the player may stand inside it.
        if (entity !== this._splatEntity) this._voxelPlacementPending = true;
        this._splatEntity = entity;
        this._splatGrid = null; // rebuild the reticle occupancy grid for the new splat
        this._splatOpacity = null;
        this._splatScales = null;
        this._splatOpacityState = 'idle';
        this.refreshSplatCollisionTransform();
    }

    /** Refresh cached splat matrices and re-bake mesh collision after transform edits. */
    refreshSplatCollisionTransform(): void {
        if (this._splatEntity) {
            this.updateSplatTransform();
            this.meshCollision?.setSplatTransform(this._splatEntity.getWorldTransform());
        } else {
            this.meshCollision?.setSplatTransform(null);
        }
    }

    /** Update cached splat transform matrices (call each frame before voxel queries) */
    private updateSplatTransform(): void {
        if (!this._splatEntity) return;
        this._splatMatrix.copy(this._splatEntity.getWorldTransform());
        this._invSplatMatrix.copy(this._splatMatrix).invert();
    }

    /** Transform a world-space position to file space using the splat entity's inverse world matrix */
    private worldToFile(worldPos: pc.Vec3, out: pc.Vec3): pc.Vec3 {
        if (this._splatEntity) {
            this._invSplatMatrix.transformPoint(worldPos, out);
        } else {
            // Fallback: default (1,-1,-1) scale assumption
            out.set(worldPos.x, -worldPos.y, -worldPos.z);
        }
        return out;
    }

    /**
     * A world-space LENGTH (radius, probe size) in file units for voxel/grid queries — the
     * conservative largest inverse-axis scale (see fileUnitsPerWorldUnit). Every file-space query
     * converts its sizes through here, so the player keeps its world size at any splat scale.
     */
    private worldSizeToFile(worldSize: number): number {
        // The no-entity fallback in worldToFile is the unscaled (1,-1,-1) mapping.
        if (!this._splatEntity) return worldSize;
        return worldSize * fileUnitsPerWorldUnit(this._invSplatMatrix.data);
    }

    /**
     * A voxel push-out (file space, from a query with `fileRadius`) as a world displacement that
     * puts a sphere of `worldRadius` back on the surface. Uniform scale: the splat matrix applied
     * to the push (full scale, not just direction). Non-uniform: also trims the conservative
     * radius's over-detection per face, so the body keeps its world width on every axis.
     */
    private filePushToWorld(filePush: { x: number; y: number; z: number }, fileRadius: number, worldRadius: number, out: pc.Vec3): pc.Vec3 {
        if (!this._splatEntity) {
            out.set(filePush.x, -filePush.y, -filePush.z); // (1,-1,-1) fallback, unscaled
            return out;
        }
        filePushOutToWorld(filePush, fileRadius, worldRadius, this._invSplatMatrix.data, out);
        return out;
    }

    private worldYPerFileY(): number {
        if (!this._splatEntity) return -1;
        return this._splatMatrix.data[5] || -1;
    }

    private fileStepHeight(): number {
        const worldPerFile = Math.abs(this.worldYPerFileY());
        return worldPerFile > 1e-6 ? this.stepHeight / worldPerFile : this.stepHeight;
    }

    /** Get the voxel collision instance (for external use, e.g. CameraControls) */
    get voxelCollisionInstance(): VoxelCollision | null {
        return this.voxelCollision;
    }
    /** Get the mesh collision instance (for sharing with CameraControls). */
    get meshCollisionInstance(): MeshCollision | null {
        return this.meshCollision;
    }
    /**
     * Get collision entities for use by other systems (e.g., CameraControls explore mode)
     */
    get collisionMeshEntities(): pc.Entity[] {
        return this.collisionEntities;
    }
    /**
     * Add a single collision entity at runtime (used by editor live-add).
     * Does NOT precompute bounds — colliderBox reads its live world transform,
     * so collision stays correct after gizmo moves.
     */
    addCollisionEntity(entity: pc.Entity): void {
        if (this.collisionEntities.includes(entity)) return;
        this.collisionEntities.push(entity);
        // Store mesh type if not already set (needed by collision logic)
        if (!(entity as CollisionEntity)._collisionMeshType) {
            (entity as CollisionEntity)._collisionMeshType = 'cube';
        }
        // Track floor entity for checkGround fast-path
        if ((entity as CollisionEntity)._collisionMeshType === 'floor') {
            this.floorEntity = entity;
        }
    }
    /**
     * Drop the colliders built from the scene data at load (createCollisionMeshes), including a
     * model still loading, and free the models they loaded. The editor calls this when its live
     * colliders take over, so a collider that was moved, deleted or replaced in the editor stops
     * colliding where it was saved.
     */
    releaseSceneCollisionMeshes(): void {
        this._sceneCollidersToken++;
        for (const [entity, asset] of this.sceneColliders) {
            this.removeCollisionEntity(entity);
            entity.destroy();
            if (asset) {
                this.app.assets.remove(asset);
                asset.unload();
            }
        }
        this.sceneColliders.clear();
    }
    /**
     * Remove a single collision entity at runtime (used by editor live-remove).
     */
    removeCollisionEntity(entity: pc.Entity): void {
        const idx = this.collisionEntities.indexOf(entity);
        if (idx !== -1) {
            this.collisionEntities.splice(idx, 1);
        }
        if (this.floorEntity === entity) {
            this.floorEntity = null;
        }
    }
    /**
     * Get whether the controller is grounded
     */
    get grounded(): boolean {
        return this.isGrounded;
    }
    /**
     * Get current velocity
     */
    getVelocity(): pc.Vec3 {
        return this.velocity.clone();
    }
    /**
     * Set position
     */
    setPosition(x: number, y: number, z: number): void {
        this.camera.setPosition(x, y, z);
    }
    /**
     * Set rotation (yaw and pitch in degrees)
     */
    setRotation(pitch: number, yaw: number): void {
        this.pitch = pitch;
        this.yaw = yaw;
        this.camera.setEulerAngles(pitch, yaw, 0);
    }
    /**
     * Set mobile joystick move input (normalized -1 to 1).
     * Left joystick: x = strafe, z = forward/back.
     */
    setJoystickMove(x: number, z: number): void {
        this.joystickMoveX = x;
        this.joystickMoveZ = z;
    }
    /**
     * Set mobile joystick look input (raw pixel deltas per frame).
     * Right joystick/touch: x = yaw delta, y = pitch delta.
     */
    setJoystickLook(x: number, y: number): void {
        this.joystickLookX = x;
        this.joystickLookY = y;
    }

    /**
     * Street-View-style one-shot look for the mobile drag-look control. Rotation is proportional to
     * RAW finger travel (screen px since the last pointermove) and applied WITHOUT dt — so it turns
     * only while the finger moves and stops the instant the finger stops (no latched velocity, unlike
     * setJoystickLook). Reuses the existing ±89 pitch clamp.
     * @param dxPixels horizontal drag delta in screen px (screenX increases to the right)
     * @param dyPixels vertical drag delta in screen px (screenY increases downward)
     */
    applyLookDelta(dxPixels: number, dyPixels: number): void {
        if (dxPixels === 0 && dyPixels === 0) return;
        const degPerPixel = this.lookSensitivity * 100 * this.touchLookMultiplier; // ≈0.3°/px
        // Turn-the-camera (FPS) look by default, matching the desktop walk mouse-look AND explore-mode
        // drag: drag right turns right, drag up looks up. invertCameraRotation flips BOTH axes (drag the
        // scene, Google Maps style) — the same helper CameraControls uses — so mobile walk honors the
        // same invert toggle as explore mode.
        const lookSign = lookAxisSign(this.invertCameraRotation);
        this.yaw -= dxPixels * lookSign * degPerPixel;
        this.pitch -= dyPixels * lookSign * degPerPixel;
        this.pitch = Math.max(-89, Math.min(89, this.pitch));
        // A deliberate look cancels auto-move / walk-to, matching the desktop mouse-look handler.
        if (Math.abs(dxPixels) + Math.abs(dyPixels) > 2) {
            if (this.autoMoveForward) this.autoMoveForward = false;
            if (this._walkTarget) this._walkTarget = null;
        }
    }
    /**
     * Walk toward a world-space target position. The character will turn and
     * walk (respecting collisions/ground) until it arrives within arrivalDist,
     * or until cancelled by manual input.
     */
    walkTo(target: pc.Vec3, arrivalDist: number = 0.5): void {
        this._walkTarget = target.clone();
        this._walkTargetArrivalDist = arrivalDist;
    }
    /**
     * Cancel any active walk-to-target navigation.
     */
    cancelWalkTo(): void {
        this._walkTarget = null;
    }
    private _debugMaterial: pc.StandardMaterial | null = null;
    private _debugVisible: boolean = false;
    private _originalMaterials: Map<number, pc.Material | null> = new Map();
    /**
     * Toggle debug visualization of collision meshes.
     * Shows semi-transparent green volumes for all collision entities.
     */
    setCollisionDebug(visible: boolean): void {
        this._debugVisible = visible;
        if (visible && !this._debugMaterial) {
            const mat = new pc.StandardMaterial();
            mat.diffuse = new pc.Color(0, 1, 0);
            mat.opacity = 1.0;
            mat.cull = pc.CULLFACE_NONE;
            mat.update();
            this._debugMaterial = mat;
        }
        for (const entity of this.collisionEntities) {
            this._setDebugVisibility(entity, visible);
        }
    }
    get collisionDebugVisible(): boolean {
        return this._debugVisible;
    }
    private _setDebugVisibility(entity: pc.Entity, visible: boolean): void {
        if (entity.render) {
            entity.render.enabled = true; // Always enable render so we can show/hide
            if (visible && this._debugMaterial) {
                // Store originals and swap to debug material
                for (const mi of entity.render.meshInstances) {
                    if (!this._originalMaterials.has(mi.id)) {
                        this._originalMaterials.set(mi.id, mi.material);
                    }
                    mi.material = this._debugMaterial;
                }
            } else {
                // Restore originals, then hide render
                for (const mi of entity.render.meshInstances) {
                    const orig = this._originalMaterials.get(mi.id);
                    if (orig) mi.material = orig;
                }
                entity.render.enabled = false;
            }
        }
        for (const child of entity.children) {
            if (child instanceof pc.Entity) {
                this._setDebugVisibility(child, visible);
            }
        }
    }

    // ── Voxel debug visualization ────────────────────────────────────────
    private _voxelDebugEntity: pc.Entity | null = null;
    private _voxelDebugVisible: boolean = false;

    get voxelDebugVisible(): boolean {
        return this._voxelDebugVisible;
    }

    /**
     * Toggle debug visualization of voxel collision octree.
     * Generates a batched wireframe-style mesh of solid voxel AABBs.
     */
    setVoxelDebug(visible: boolean): void {
        this._voxelDebugVisible = visible;

        if (!visible) {
            if (this._voxelDebugEntity) {
                this._voxelDebugEntity.enabled = false;
            }
            return;
        }

        // If entity already built, just re-enable
        if (this._voxelDebugEntity) {
            this._voxelDebugEntity.enabled = true;
            return;
        }

        // Build the debug entity from the loaded voxel collision data
        if (!this.voxelCollision) {
            console.warn('[CharacterController] No voxel collision loaded, cannot show debug');
            return;
        }

        const aabbs = this.voxelCollision.collectSolidAABBs(100_000);
        const count = aabbs.length / 4;
        if (count === 0) {
            console.warn('[CharacterController] Voxel octree has no solid voxels');
            return;
        }
        console.log(`[CharacterController] Building voxel debug mesh: ${count} solid voxels`);

        // Build a batched mesh of cubes using line primitives for wireframe effect
        // Each cube = 12 edges = 24 vertices (2 per line)
        const EDGES_PER_CUBE = 12;
        const VERTS_PER_CUBE = EDGES_PER_CUBE * 2;
        const positions = new Float32Array(count * VERTS_PER_CUBE * 3);

        // Cube edge vertex offsets (pairs of corners defining 12 edges)
        const edgePairs = [
            // Bottom face
            [0,0,0, 1,0,0], [1,0,0, 1,0,1], [1,0,1, 0,0,1], [0,0,1, 0,0,0],
            // Top face
            [0,1,0, 1,1,0], [1,1,0, 1,1,1], [1,1,1, 0,1,1], [0,1,1, 0,1,0],
            // Vertical edges
            [0,0,0, 0,1,0], [1,0,0, 1,1,0], [1,0,1, 1,1,1], [0,0,1, 0,1,1],
        ];

        for (let i = 0; i < count; i++) {
            // Keep raw file-space coords — parented to splat entity which handles the transform
            const cx = aabbs[i * 4];
            const cy = aabbs[i * 4 + 1];
            const cz = aabbs[i * 4 + 2];
            const h = aabbs[i * 4 + 3];
            const baseVert = i * VERTS_PER_CUBE * 3;

            for (let e = 0; e < 12; e++) {
                const ep = edgePairs[e];
                const vi = baseVert + e * 6;
                positions[vi]     = cx + (ep[0] * 2 - 1) * h;
                positions[vi + 1] = cy + (ep[1] * 2 - 1) * h;
                positions[vi + 2] = cz + (ep[2] * 2 - 1) * h;
                positions[vi + 3] = cx + (ep[3] * 2 - 1) * h;
                positions[vi + 4] = cy + (ep[4] * 2 - 1) * h;
                positions[vi + 5] = cz + (ep[5] * 2 - 1) * h;
            }
        }

        // Create the mesh
        const mesh = new pc.Mesh(this.app.graphicsDevice);
        mesh.setPositions(positions);
        // WebGPU + skybox scenes: StandardMaterial variants demand vertex_normal;
        // a position-only buffer fails CreateRenderPipeline and blanks every frame
        // (grey viewport). Provide unit normals and disable env sampling.
        const normals = new Float32Array(positions.length);
        for (let ni = 1; ni < normals.length; ni += 3) normals[ni] = 1;
        mesh.setNormals(normals);
        mesh.update(pc.PRIMITIVE_LINES);

        // Green material for voxel debug
        const mat = new pc.StandardMaterial();
        mat.diffuse = new pc.Color(0, 0.8, 0.2);
        mat.emissive = new pc.Color(0, 0.8, 0.2);
        mat.useLighting = false;
        mat.useSkybox = false;
        mat.update();

        const mi = new pc.MeshInstance(mesh, mat);
        const entity = new pc.Entity('voxel-debug');
        // Find the dedicated VoxelDebug layer so the overlay renders above gsplats
        const voxelLayer = this.app.scene.layers.getLayerByName('VoxelDebug');
        entity.addComponent('render', {
            type: 'asset',
            meshInstances: [mi],
            castShadows: false,
            receiveShadows: false,
            ...(voxelLayer ? { layers: [voxelLayer.id] } : {}),
        });

        // Parent to splat entity so debug mesh inherits splat position/scale/rotation
        if (this._splatEntity) {
            this._splatEntity.addChild(entity);
        } else {
            this.app.root.addChild(entity);
        }
        this._voxelDebugEntity = entity;
    }

    /**
     * Destroy cached voxel debug entity (call when voxel data changes).
     */
    clearVoxelDebug(): void {
        if (this._voxelDebugEntity) {
            this._voxelDebugEntity.destroy();
            this._voxelDebugEntity = null;
        }
        this._voxelDebugVisible = false;
    }
    /**
     * Find the ground height at a world-space position.
     * Returns the Y coordinate of the ground surface, or null if no ground found.
     * Used by VR teleportation to find valid landing spots.
     */
    findGroundAt(position: pc.Vec3): number | null {
        return this.checkGround(position);
    }
}
